// src/encounter/assessment-plan-view.tsx
// A/P as Flutter's two-column workspace (PROD-1418), laid out by the DS `AssessmentPlanLayout`
// template: LEFT the DS `ApSearchRail` through its PA container (ap-search-rail.tsx — the hooks,
// query state and row mappers; tool strip → search+Suggest → favourites → suggested → global) +
// CENTER DS AssessmentPlanCanvas (Dx cards,
// drag-reorder, external-drop target) + per-type order sections through DS ApOrderCard. A row's ＋
// adds a diagnosis or opens the matching order picker seeded; dropping onto the canvas still works.
// The app supplies allowedOrderTypes (filtered to the DS set) + roleLabel + the working diagnoses
// state; diagnosis search moved off the old top "Add Diagnosis" card into the panel's Diagnosis tab.
//
// Seeded, drop-anywhere order flow (PROD-1419 — Flutter addItemInApSection, assessment_plan_screen
// .dart:130): dropping an order opens the matching picker SEEDED with the dragged row (payload.item)
// — no re-search. Medication skips the search widget and goes straight to the dose editor with the
// dragged MedicationOption; lab/imaging/procedure open pre-selected (initialItem); referral pre-fills
// its target; vaccine opens straight in the request editor. The drop attaches to the dropped-on card
// OR (drop anywhere) the primary/first Dx; only with NO diagnosis at all do we show Flutter's "add a
// diagnosis first" hint. Each dialog owns its OWN search hook (the view passes providerId + gender).
// On confirm ({ planOrder, payload, dxIds }) the view surfaces these up via onAttachOrder(dxIds,
// planOrder, payload) — dxIds is the multi-select's full set (PROD-1801), not a single parent.
// onAttachMedication is a legacy fallback for hosts without onAttachOrder.
//
// Falls back to the v1 placeholder onAddOrder for any type whose picker can't run (no providerId,
// or the host didn't wire the corresponding capability) so nothing crashes.
//
// Open-state is the only local UI state: { dxId, type, initialItem? } = which picker is open, for
// which Dx, seeded with which dragged row. Source: orders_tab.dart / assessment_plan_screen.dart.
import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useDesignV2, V2_EYEBROW } from './lib/design-v2';
import {
  AssessmentPlanCanvas,
  AssessmentPlanLayout,
  EncounterDxCard,
  ModalDropdown,
  PatientEducationSection,
  MedicationSearchWidget,
  Modal,
  Banner,
  Button,
  EncounterSectionHeader,
  EncounterSectionChrome as DsEncounterSectionChrome,
  type DxCard,
  type OrderType as DsOrderType,
  type MedicationResult,
  type CodeSearchResult,
  type PlanOrder,
  type ApSearchCategory,
  type ApSearchItem,
  type ApDragPayload,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  hasApDragData,
  readApDragData,
} from '@scripsteam/scrips-signal-ds';
import { matchProblem, type PatientProblemLite } from './lib/patient-problems';
import type { ActiveMedicationItem } from './encounter-workflow';
import { ApNoteDialog } from './ap-note-dialog';
import { ApInlineNarrative } from './ap-inline-narrative';
import type { NoteMacroScope } from './services/use-note-macros';
import type { NoteMacro } from './lib/smart-phrase';
import { ApOrderRow, ApMedLifecycleActions } from './ap-order-row';
import { SectionIconAction } from './section-icon-action';
import { AlertTriangle, MessageSquare, Pencil, Plus, Search, Trash2, type LucideIcon } from 'lucide-react';
import { DiagnosisMultiSelect } from './diagnosis-multi-select';
import { articleBlurb, type EducationArticle } from './lib/to-education-article';
// THE LEFT RAIL, now a local component (2026-08-06). It composed `ApFavouritesPanel`; the Claude
// Design's rail anatomy — a 7×(32×30) hairline-divided tool strip with a literal `Dx` cell, Suggest
// BESIDE the field, a per-row confidence meter, and a GLOBAL block that is not gated on a typed
// query — has no expression in that component's props. See ap-search-rail.tsx for the full rationale
// and the four DS graduation candidates it records.
import { ApSearchRailContainer } from './ap-search-rail';
// PROD-3041 (frame v2, inside the note): the ONE add line replaces the rail above. See ap-add-line.tsx.
import { ApDiagnosisAddLine, ApOrderAddLine, type AddLinePick } from './ap-add-line';
import { ApOrderSlashAddLine, ApSlashAddLine } from './ap-slash-add-line';
import type { ApBundle } from './services/use-bundles';
import { LabAddDialog } from './lab-add-dialog';
import { otherLabsOnPlan } from './lib/lab-catalogue';
import { ImagingAddDialog } from './imaging-add-dialog';
import { ProcedureAddDialog } from './procedure-add-dialog';
import { ReferralAddDialog } from './referral-add-dialog';
import { referralDialogSeed } from './lib/referral-edit-seed';
import { MedicationDoseDialog } from './medication-dose-dialog';
import { PrescriptionCards, type PrescriptionCardItem } from './prescription-cards';
import { MED_FREQUENCY, MED_FREQUENCY_OPTIONS } from './lib/medication-frequency';
import type { OrderSetApplyState } from './order-sets/use-order-set-apply';
import { isOrderSetDragItem } from './order-sets/order-sets-rail-model';
import { AppliedMedicationChecks, OrderSetCanvasBlocks } from './order-sets/order-set-canvas-blocks';
import { groupOrdersByDiagnosis } from './order-sets/ap-dx-groups';
import { ApDxOrderBody } from './order-sets/ap-dx-order-groups';
import type { MedicationDose } from './lib/to-medication-order';
import { VaccineAddDialog } from './vaccine-add-dialog';
import { SaveAsOrderSetAction } from './order-sets/save-as-order-set-action';
import { labPlanDetail } from './lib/lab-plan-row';
import type { CodedOrderPayload, OrderPayloadMap } from './lib/order-payload';
import type { DrugInteractionMedInput } from './lib/to-drug-interaction-input';
import { toMedicationPick, type MedicationPick } from './lib/to-medication-result';
import type { ReferralTargetInput } from './lib/to-referral-order';
import type { ChartReads } from './lib/to-chart-snapshot';
import type { ImmunizationCoding } from '@/types/patient-chart-api';
import {
  payloadToCodeSearchResult,
  payloadToMedicationPick,
  payloadToMedicationDose,
  vaccinationEntryToCoding,
  vaccinationEntryToDraft,
  vaccinationEntryToValueSet,
} from './lib/payload-to-seed';
import type { VaccineValueSet } from '@/shared/services/use-vaccination-search';
import { CarePlanReadout } from './care-plan-readout';
import { AssignCarePlanAction } from './assign-care-plan';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { L360ActivationPanel } from './l360/l360-activation-panel';
import { ApVisitComplaint, type VisitComplaintState } from './ap-visit-complaint';
import { ambientSearchText } from './ambient/to-order-seed';

/** PROD-3041: the DOM id of the Assessment block's "Add diagnosis" input (the held-order banner focuses it). */
export const ADD_DIAGNOSIS_INPUT_ID = 'ap-add-diagnosis';

/** Which picker is open, for which Dx, and (when seeded from a drag OR editing) the seed data. */
interface OpenPicker {
  dxId: string;
  type: DsOrderType;
  /** PROD-3423 (`explicitDxLink`): `dxId` is the primary diagnosis as a FALLBACK, not the clinician's choice (Add order, a seed, a
   *  drop that is not on a card). With 2+ diagnoses the dialog then opens with none selected. */
  dxDefaulted?: boolean;
  /** The dragged panel row (`payload.item`), typed per-branch at render (PROD-1419). */
  initialItem?: unknown;
  /** Editing an existing order (PROD-1433): its id (confirm UPDATES it, keeping id + dxIds). */
  editingOrderId?: string;
  /** The saved payload being edited — reconstructs each dialog's pre-fill at render. */
  editPayload?: CodedOrderPayload;
  /**
   * Dose fields from a DICTATED medication, pre-filling a NEW order (not an edit).
   *
   * Distinct from `editPayload`, which reconstructs a SAVED order. This is what the clinician said
   * out loud, parsed by `to-dictated-dose` and still unconfirmed — it opens the form filled in and
   * writes nothing until they save. Before this, ambient's dose/route/frequency/duration were
   * dropped on the way through the catalogue search and the dialog opened blank.
   */
  dictatedDose?: MedicationDose;
}

/** The picked row's own name for the held-order line, never a guess at what it is. */
function heldOrderName(item: unknown): string {
  const src = item as { name?: string; display?: string; displayName?: string } | undefined;
  return (src?.name ?? src?.display ?? src?.displayName ?? '').trim() || 'This order';
}

/** Adapt a dragged referral CodeSearchResult → the referral dialog's `target` input. */
function referralTargetFromItem(item: unknown): ReferralTargetInput | undefined {
  const r = item as CodeSearchResult | undefined;
  if (!r || (!r.display && !r.code)) return undefined;
  return { name: r.display ?? r.code ?? '', ...(r.code ? { code: r.code } : {}), ...(r.system ? { system: r.system } : {}) };
}

export interface AssessmentPlanViewProps {
  diagnoses: DxCard[];
  /** Top-level orders (v2 PROD-1422), each linking 1..N diagnoses via dxIds. */
  orders: PlanOrder[];
  /** DS-filtered order types the user may attach (see toCanvasOrderTypes). */
  allowedOrderTypes: DsOrderType[];
  roleLabel?: string;
  readOnly?: boolean;
  /**
   * Neutral, muted notice rendered above the canvas (e.g. "Loading assessment…" while the initial
   * GET is in flight, or "Saving…" during a save). Display-only — pairs with readOnly to make the
   * blocked state legible. Absent → no notice.
   */
  notice?: ReactNode;
  /** Practitioner id — scopes the per-type catalog searches the dialogs own. */
  providerId?: string;
  /** The provider's saved A&P bundles (PROD-1839). Fetched by the HOST — the id that route wants
   *  is `user.id`, NOT the `providerId` above; see services/use-bundles.ts. */
  bundles?: ApBundle[];
  bundlesLoading?: boolean;
  /** Apply a bundle's saved A&P to this canvas. Absent → bundle rows render without the ＋. */
  onImportBundle?: (bundle: ApBundle) => void;
  /** Archive a bundle, after the rail's confirm (PROD-2697). Absent → no archive affordance on the row. */
  onDeleteBundle?: (bundle: ApBundle) => void;
  /**
   * A pick made in the page-level ADD ORDER drawer, handed to this view to finish.
   *
   * Order setup is a per-type dialog (dose, lab panel, radiology…) seeded from the picked row, and
   * that dialog's state lives HERE. Rather than lift it to the page — a large refactor for one
   * caller — the drawer forwards its pick and the view opens its own picker. Consumed once, then
   * cleared through `onSeedConsumed` so re-rendering cannot reopen it.
   */
  /**
   * "Add Order" — the sparkle icon, opening the shared drawer. Named `onOpenAddOrder` because
   * `onAddOrder` is already taken on this view by the v1 per-Dx order-add callback.
   */
  onOpenAddOrder?: () => void;
  /** "Add to favourites" — the magnifier. */
  onAddToFavourites?: () => void;
  seedRequest?: {
    item: unknown;
    category: DsOrderType;
    /** Dictated dose fields to open the dose dialog with — see `OpenPicker.dictatedDose`. */
    dictatedDose?: MedicationDose;
  } | null;
  onSeedConsumed?: () => void;
  /**
   * `frameV2` (1 Oct, "orders are not add"): keep an order the clinician picked while the plan has
   * NO diagnosis, instead of dropping it. The host stores it as the next `seedRequest`, so it
   * survives a step change, and this view opens its form once a diagnosis exists. Absent (or the
   * switch OFF): today's behaviour, a hint and nothing kept.
   */
  onHoldOrder?: (seed: { item: unknown; category: DsOrderType; dictatedDose?: MedicationDose }) => void;
  /** How many picks the host holds, the head being `seedRequest` (`frameV2`; the held line counts them). */
  pendingSeedCount?: number;
  /**
   * PROD-2722: the header's ⋯ "More A/P actions" with "Save as order set" (v3 board 7). The host
   * sets it only with order sets on for a physician (`canApplyOrderSets`). Absent → no ⋯.
   */
  saveAsOrderSet?: boolean;
  /** True while an archive is in flight — disables the row's archive affordance. */
  deletingBundle?: boolean;
  /** Patient id — enables the inline drug-interaction check in the medication dose dialog
   *  (Flutter getDrugInteractions `subject`). Absent → no interaction check runs. */
  patientId?: string;
  /** Patient gender — narrows sex-specific lab value-set panels (optional). */
  gender?: string;
  onReorder?: (orderedDxIds: string[]) => void;
  /** Promote a Dx to primary (host demotes the rest — one-primary). v2 PROD-1422. */
  onSetPrimary?: (dxId: string) => void;
  /** Change which diagnoses an order justifies (multi-select, min 1). v2 PROD-1422. */
  onLinkOrderDx?: (orderId: string, dxIds: string[]) => void;
  /** v1-placeholder add-order (kept for the no-picker fallback path only; NOT a per-Dx rail in v2). */
  onAddOrder?: (dxId: string, type: DsOrderType) => void;
  /** Remove a top-level order by id (v2 PROD-1422 — was (dxId, orderId)). */
  onRemoveOrder?: (orderId: string) => void;
  onRemoveDx?: (dxId: string) => void;
  /** Add a coded diagnosis picked from the ICD-10 search (DS CodeSearchField). */
  onAddDx?: (dx: CodeSearchResult) => void;

  // ── Coded order pickers (PROD-1297) ─────────────────────────────────────────
  /**
   * Attach a coded order (lab / imaging / procedure / referral) picked from its dialog.
   * The view appends `planOrder` to the Dx and the host records `payload` in the side-map.
   */
  /**
   * Attach a coded order to the diagnoses the clinician linked in the dialog (PROD-1801).
   * Widened from a single `dxId`: the order dialogs now carry a `DIAGNOSIS (n) *` multi-select, and
   * the save mapper fans an order out to every entry, so the whole set must reach the host.
   */
  onAttachOrder?: (dxIds: string[], planOrder: PlanOrder, payload: CodedOrderPayload) => void;

  /**
   * The A/P SECTION narrative (PatientAssessment.notes / notePlainText) — Flutter's commentButton.
   * Absent onChange ⇒ the Added Notes block renders read-only, which is also the readOnly path.
   *
   * The note is a PAIR on the wire: `notes` is a Quill Delta (what Flutter's QuillStaticView
   * renders) and `notePlainText` is the flattened text. `assessmentNote` is the plain half — it
   * is what the read-back card shows; `assessmentNoteDelta` is the raw `notes` value and is what
   * seeds the editor so formatting survives a round-trip. A host that only holds plain text can
   * omit the delta: the editor falls back to seeding from the text.
   */
  assessmentNote?: string;
  assessmentNoteDelta?: string;
  /**
   * `plain` → `notePlainText`, `delta` (a bare Quill ops-array JSON string) → `notes`.
   * Writing plain text into `notes` renders BLANK in the Flutter client — see
   * src/dashboard/quill-delta.ts — so hosts must persist the delta, not the text, into `notes`.
   */
  onAssessmentNoteChange?: (note: string, delta: string) => void;
  /**
   * The page's already-loaded reads the narrative editor's CHART pieces quote (latest vitals, the
   * allergy list) — with their load state, so a failed read is never shown as an empty list
   * (`lib/to-chart-snapshot.ts`). Absent → chart pieces resolve as "Loading…".
   */
  noteChartReads?: ChartReads;
  /**
   * TS-4 (PROD-2820): the visit's specialty and type, so the narrative editor's phrase menu offers the
   * phrases scoped to this visit (and every unscoped one). Absent → the unfiltered list, as before.
   */
  noteMacroScope?: NoteMacroScope;
  /**
   * PROD-3043 · frame v2 only: the phrases the inline "Added notes" line offers, supplied (stories, tests).
   * Absent (the app) → fetched for this visit with `noteMacroScope`, exactly as the pop-up fetched them.
   */
  noteMacros?: readonly NoteMacro[];
  /** PROD-2947 Part 2 · frame v2 (G8): the triggers the "Added notes" line offers FIRST (the starter-map order for
   *  the visit's chief complaint). A menu order only — nothing is inserted. Absent → the library's own order. */
  noteSuggested?: readonly string[];

  /**
   * dxId → its preserved coding. Used for the card's SYSTEM label: SNOMED diagnoses are addable
   * (new_ap_bloc.dart:681-693) and were being mislabelled as ICD-10 because the label was hardcoded.
   */
  dxCoding?: ReadonlyMap<string, { code?: string; display?: string; system?: string }>;

  /**
   * Whether this tenant's diagnosis system is AzureAI — the gate Flutter puts on its Suggest button
   * (assessment_plan_search_list_widget.dart:872-900, `resourceBasedIntegration.diagnosisSystem`).
   * PERMISSION/CONFIG DATA from the app, never a tenant name checked in here.
   *
   * LIVE AGAIN (2026-08-06). It was briefly inert: Suggest had been built into the standalone
   * diagnosis-search block ABOVE the tab strip, and when that second field was deleted for Flutter
   * parity (Flutter has ONE search in this rail) Suggest went with it. That was the wrong half to
   * drop — Flutter's Suggest is not a second search, it is a BUTTON BESIDE the one search
   * (`searchBar()`, assessment_plan_search_list_widget.dart:850-903), and it reads that field's text.
   *
   * It is rendered against the rail's OWN query, on the Diagnosis tab only, exactly as Flutter gates
   * it (diagnosis tab, not Bundles, `resourceBasedIntegration.diagnosisSystem == 'AzureAI'`).
   *
   * THE PLACEMENT DELTA IS CLOSED (2026-08-06). It used to sit in a block BELOW the whole panel,
   * because `ApFavouritesPanel` has no search-row action slot; the local `ApSearchRail` puts the
   * button back where Flutter and the design both put it — inline, beside the one field.
   */
  aiDiagnosisEnabled?: boolean;

  // ── Order edit (PROD-1433) ──────────────────────────────────────────────────
  /** The saved payloads keyed by order id — read to pre-fill the edit dialog. */
  orderPayloads?: OrderPayloadMap;
  /**
   * Update an existing order in place (keep its id); confirms an edit instead of appending. `dxIds` is
   * the edit form's diagnosis selection (PROD-2886): the host re-links the order to it, or keeps the
   * existing links when it is absent or empty.
   */
  onUpdateOrder?: (orderId: string, planOrder: PlanOrder, payload: CodedOrderPayload, dxIds?: string[]) => void;

  // ── Medication order picker (PROD-1165) ─────────────────────────────────────
  // When wired (onMedicationQueryChange present), tapping "+ Med" on a Dx opens a
  // medication search (DS MedicationSearchWidget) instead of the v1 placeholder; the
  // host supplies results (fixtures now; the live useMedicationSearch hook is a follow-up).
  medicationResults?: MedicationResult[];
  medicationQuery?: string;
  onMedicationQueryChange?: (query: string) => void;
  /** Attach the picked medication as a PlanOrder on the diagnosis. */
  onAttachMedication?: (dxId: string, result: MedicationResult) => void;

  // ── Patient education (PROD-1454) — Dx-less article tab + canvas section ─────────────────────
  /** The attached education articles (rendered as the canvas EDUCATION section). */
  articles?: EducationArticle[];
  /** Add a picked article (host dedups by display). Presence enables the Education search tab. */
  onAddArticle?: (article: EducationArticle) => void;
  /** Remove an attached article (by id when saved, else its index). */
  onRemoveArticle?: (idOrIndex: string | number) => void;

  // ── Four moments (PROD-1656 spec §10, mounting wave 2026-07-24) ─────────────────────────────
  /**
   * The patient's ACTIVE problem list (chart-detail conditions, lite shape). Feeds:
   *  • M2 — the patient-aware dx picker groups ("Patient's active problems" first, badged);
   *  • M2b/M3 — the EncounterDxCard identity chip ("On problem list · since YYYY") + the
   *    "Resolve with this visit" action. Day-1 identity = code-string equality (spec §10.2
   *    honesty note; record identity lands with the PROD-1684 memory column).
   * Absent → the picker searches the catalogue only and no dx carries an identity chip.
   */
  patientProblems?: PatientProblemLite[];
  /**
   * Show the sign-promotion DISCLOSURE on a new dx — the "Joins problem list at sign" chip and,
   * with it, the "Don't add to problem list" stewardship opt-out (the DS retires that action with
   * the chip). DEFAULT OFF.
   *
   * WHY OPT-IN: the disclosure is a React-side addition from the four-moments wave (PROD-1801
   * ruling C, Samer 2026-07-24 — auto-promote + stewardship). It has no counterpart in the Flutter
   * PA A/P, whose Dx card carries index · term · code · system · Primary/Secondary and nothing
   * else. While the frame is being judged for Flutter parity, an extra chip on every new diagnosis
   * reads as drift, so the default matches the source and a host that WANTS the stewardship
   * surface asks for it explicitly.
   *
   * It is deliberately gated rather than deleted: auto-promotion at sign is real behaviour, and a
   * silent automatic write to the patient's problem list with no disclosure and no opt-out is the
   * exact failure ruling C exists to prevent. Turning this on is how that promise is kept.
   */
  showProblemListDisclosure?: boolean;
  /** Dx ids the clinician opted OUT of the sign-promotion (stewardship — ruling C). */
  keptOffDxIds?: ReadonlySet<string>;
  /** Dx ids resolved with THIS visit (receipt state — actions retire on the card). */
  resolvedNowDxIds?: ReadonlySet<string>;
  /** Stewardship opt-out ("Don't add to problem list") for an auto-joining NEW dx. */
  onKeepOffDx?: (dxId: string) => void;
  /** One-tap resolve for an ON-LIST dx (same resolve write the rail uses; date = today). */
  onResolveDxWithVisit?: (dxId: string) => void;
  /**
   * The undo receipt (host-composed UndoSnackbar) — anchors to the bottom of the diagnosis
   * column (approved frame f8/f5 placement). The HOST owns the moment state + undo semantics.
   */
  dxReceipt?: ReactNode;

  // ── ACTIVE MEDICATIONS (design 725-765) ─────────────────────────────────────────────────────
  /**
   * This encounter's Rx orders that are not on the plan, rendered as the canvas's ACTIVE
   * MEDICATIONS run below the plan's orders (Flutter parity: `toApActiveMedications`,
   * lib/to-ap-active-medications.ts). NOT the chart list: a reported or continuing chart
   * medication is never drawn here as an order. This is not plan state either.
   *
   * Absent or empty → the whole section is omitted (no empty-state chrome for a list the
   * host never wired). It never participates in the A/P save mapper.
   */
  activeMedications?: ActiveMedicationItem[];
  /**
   * The row's four MEDICATION-LIFECYCLE actions (design 745-762: Change · Refill · Discontinue ·
   * Delete). All four RENDER, always — the design's cluster is a fixed shape and hiding one
   * silently redraws the row. A handler-less action renders DISABLED, so the surface is honest
   * about what is not wired yet without ever binding a live-looking button to a no-op.
   *
   * Each is a real server write (Scrips.Patient EPriscriptionController: PUT
   * Update/{patientRxOrderId} · PostRefill · PostStopMedication) and only the STOP path has a
   * React hook in this repo so far — services/use-save-medication.ts `buildStopMedicationBody`,
   * driven from the pre-encounter rail. `onDeleteMedication` has no endpoint of its own that
   * Discontinue does not already cover, so expect it to stay unwired the longest.
   */
  onChangeMedication?: (medicationId: string) => void;
  onRefillMedication?: (medicationId: string) => void;
  onDiscontinueMedication?: (medicationId: string) => void;
  onDeleteMedication?: (medicationId: string) => void;

  /**
   * ORDER SETS (PROD-2712) — the page's `useOrderSetApply` state, passed only when the
   * `VITE_ORDER_SETS_APPLY` flag is on and the user may order. Absent → the A/P is exactly as
   * before (the rail's Bundles, no order-set blocks, no order-set drag handling).
   */
  orderSets?: OrderSetApplyState;

  /**
   * The visit's chief complaint, as CONTEXT for the assessment (frame v2 only; `?ef=frameV2`). The
   * host passes the read of the SAME complaint document the HPI block draws. A readout: it never
   * adds or pre-fills a diagnosis (authority §4 rules 3 and 4). Absent, or the switch OFF → nothing
   * renders, so today's A&P DOM is unchanged. Inside the note (`inNote`) it is not drawn either: the
   * note shows the chief complaint once, in HPI (PROD-3041).
   */
  visitComplaint?: VisitComplaintState;
  /**
   * TS-3 (frame v2 only; Samer A6 "A and P separate", A7 "inline"): draw ONE part of this A/P.
   *   · `assessment` — the A section: the visit's chief complaint as context, the assessment narrative,
   *     the confirmed diagnoses (add, priority, remove, reorder), with the diagnosis search inline.
   *   · `plan` — the P section: the orders, each under the first diagnosis it justifies, then the care
   *     plan, education and this visit's active medications, with the order search inline; held orders
   *     and every order form live here.
   * Same props, same state from the host, same save path (PatientAssessmentPlan / Send Orders): this is
   * rendering only. Absent → the whole A/P exactly as before (OFF and every existing caller).
   */
  part?: 'assessment' | 'plan';
  /**
   * Frame v2 only: the A/P part (`part`) is drawn inside a note block. Its action row then shows only
   * the LIVE actions (FV2-06: no "coming soon" control). The title rule is the A/P split's alone: a
   * part draws no title (ApPartFrame), so frame v2 has ONE mechanism for the A/P's headers (the
   * independent review, 6 Oct). Absent (OFF, the old frame) → today's section chrome, exactly as before.
   */
  inNote?: boolean;
}

const CARD_CLASS =
  'bg-surface-card border border-solid border-border-default rounded-lg p-6';
/** Design sheet v1.1 §1 Surfaces (switch ON only): the card lifts — card border + `--shadow-2`. */
const CARD_CLASS_V2 =
  'bg-surface-card border border-solid border-border-card shadow-2 rounded-lg p-6';

// ── E1 A&P frame numbers (ROUND-4 / PROD-1588, node 7624:451752 · ap.spec.json) ─────────────────
// Canvas group label — "Fonts/New/Section Heading 12/Blue": Roboto Bold 12 / lh normal /
// ls +0.504px (= 0.042em) / uppercase / legacy Scrips Title Blue → token --color-brand-depth
// (ap.spec.json palette map).
/**
 * MEDICATION ROW SIGNALS — Flutter renders a priority chip + drug-interaction glyph inside the row's
 * `additionalWidget` (assessment_plan_screen.dart:823-843). ApOrderCard has no slot for either, and
 * Andrew ruled "exactly what in flutter, ignore ds" (31 Jul) after I twice deferred to a DS variant.
 * So they are overlaid locally, top-right of the row, rather than left absent.
 *
 * Overlay rather than a fork of the card: the DS component still owns every pixel it draws, so a DS
 * update cannot silently diverge from a copy of it here. If a DS variant lands later, delete this and
 * pass props — the signals come from the payload either way.
 */
/**
 * PER-TYPE IDENTITY RAIL — Flutter gives each order section its own left-rail colour
 * (assessment_plan_screen.dart:761 labs · 850 meds · 932 radiology · 994 vaccination · 1050 procedure ·
 * 1169 referral). The DS maps ALL SIX to one brand token — while its own docstring claims "six calm
 * identities", a contradiction worth a DS issue — so a dense plan reads as one undifferentiated stack
 * and the type must be decoded from a 24px glyph.
 *
 * Andrew ruled "exactly what in flutter, ignore ds" (31 Jul), so the rail is overlaid locally.
 *
 * Worth recording accurately: Flutter does NOT use six distinct colours. It uses FOUR — labs and
 * procedure share the light blue, radiology and vaccination share black. Mapped BY ROLE to tokens
 * rather than copying Flutter's literals (#FCC001 becomes the warning token, `Colors.black` becomes
 * text-primary), because hardcoded hex is the one rule that does not bend.
 */
/**
 * SUGGESTED-DIAGNOSIS ROW + CONFIDENCE BAR moved out with the rail — see ap-search-rail.tsx.
 * Flutter draws it as the `codeTitlePercentage` cell (ap_item_view_cell.dart:109-181); the design
 * draws it at EncounterScreen.dc.html:607-624. Both now live next to the only thing that renders
 * them, instead of as orphan style constants in the host view.
 */

/**
 * PER-TYPE ACCENT BAR + TYPE CHIP now live on the row itself — see `ap-order-row.tsx`, which
 * replaced the DS `ApOrderCard` on this surface (E1 design EncounterScreen.dc.html:685-723).
 *
 * The overlay that used to live here (an absolutely-positioned 3px rail painted ON TOP of the DS
 * card, plus an absolutely-positioned signal cluster) is gone with it: the local row draws the rail
 * as a real flex child and takes the clinical signals through its `signals` slot, so nothing is
 * stacked over a component that doesn't know about it any more.
 */
/** Sentence case ("Routine", "Urgent"); STAT and ASAP are acronyms and keep their capitals. Values: FHIR request priority (routine, urgent, asap, stat). */
const priorityLabel = (p: string): string =>
  ['stat', 'asap'].includes(p.toLowerCase()) ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
/** Flutter: coloredBackgroundText(priority, bgColor: designColor2, 2/4 padding). */
// borderRadius 3px has no DS radius token — kept as an arbitrary value (see W3 conversion report).
const PRIORITY_CHIP_CLASS =
  'py-0.5 px-1 rounded-tag text-size-2xs font-medium ' +
  // Token, not a literal — this was a hardcoded white hex, which the colour gate rejects.
  'text-text-inverse bg-status-error';

/**
 * A/P column eyebrow ("ADDED NOTES" / "ACTIVE MEDICATIONS").
 *
 * The EncounterScreen design sets these at 10px/700, 0.08em, uppercase, --color-text-hint
 * (lines 647 and 726) — quieter than the 12px brand-depth heading this used to be, so the
 * eyebrows read as labels above the content rather than competing with the section title.
 *
 * KNOWN REMAINING DELTA: the canvas's own "DIAGNOSES" label is still 12px brand-depth. It is
 * rendered inside the DS AssessmentPlanCanvas, which exposes neither a label prop nor a testid
 * to hook (probed 2026-08-07: the label is an unmarked `span` four unmarked divs deep), so
 * matching it needs a DS change rather than a host override.
 */
/**
 * R9 — every eyebrow on the deployed A/P canvas is `boldLabelTextStyle(12, scripsTitleBlueColor)`
 * with NO letterSpacing (`assessment_plan_screen.dart:641-643, 1238`;
 * `common_encounter_component_base_tab.dart:245`). `#233D93` is a recorded FREEZE-MANIFEST
 * mapping to `--color-brand-depth`. The 10px / +0.08em / text-hint values came off the design
 * node and made every canvas label read two steps quieter than the source.
 * The constant keeps its name — the "12" in it is now true.
 */
const SECTION_HEADING_12_CLASS =
  'text-size-eyebrow font-bold uppercase text-brand-depth';
/** Design sheet v1.1 §1 (switch ON only): a canvas label is the DS caps label in text-secondary —
 *  labels whisper, brand stays on actions (H9). */
const SECTION_HEADING_12_CLASS_V2 = V2_EYEBROW;
/** ON: the Added-notes card is an inset (card border, no lift). */
const ADDED_NOTES_CARD_CLASS =
  'flex items-start gap-3 py-3 pr-3 pl-4 bg-surface-card border border-solid border-border-strong rounded-md';
const ADDED_NOTES_CARD_CLASS_V2 =
  'flex items-start gap-3 py-3 pr-3 pl-4 bg-surface-card border border-solid border-border-card rounded-md';
/** The app icon button (icon pass, 16 Oct): neutral in both modes, like every row bin; it was red at rest. */
const NOTES_DELETE_BTN_CLASS = 'icon-action';
/** ON: an idle destructive icon is neutral at rest, red only on hover (§1 Status). */
const NOTES_DELETE_BTN_CLASS_V2 = NOTES_DELETE_BTN_CLASS;

// Shared by the bundle-name notice, the drop-hint status line and the priority notice — all
// three were the identical inline style object.
const STATUS_LINE_CLASS = 'm-0 text-size-sm text-text-secondary';

/*
 * The imaging ⇄ radiology naming seam is GONE: it existed only to translate this app's
 * `ApSearchCategory` into the DS favourites panel's `ApFavCategory`. `ApSearchRail` speaks the app
 * vocabulary directly and maps to the Flutter glyph names in its own table.
 */

/**
 * ADDED NOTES — the E1 block (node 7624:577856, "Added Notes" symbol, 584×99):
 *   · label row: Section Heading 12 (Bold 12 / +0.504px / uppercase / legacy Title-Blue →
 *     --color-brand-depth), h16
 *   · 8px below: a white note card (Card shadow '2' — 0 2px 4 at 5% black, radius 6, min-h 75)
 *     with the note text (Roboto Regular 15 / lh 1.432 / ls −0.45px → −0.03em / legacy Text-Blue →
 *     --color-text-secondary) padded 16 left, and the pencil (24-box, legacy Primary-Light-Blue —
 *     REJECTED per DS-016 → --color-teal-500) + trash (24-box, legacy red → --color-status-error)
 *     INSIDE the card at the right (16px gap, 16px right pad), vertically centred.
 * NOW LIVE (PROD-1801, 30 Jul). It shipped disabled with a placeholder reading "available once the
 * A&P notes round-trip is wired" — but the wire field existed all along, and the save mapper was
 * writing `notes: null` over it on every autosave. So this wasn't a missing feature, it was DESTROYING
 * the A/P narrative a clinician had written in Flutter on the same encounter.
 *
 * Plain text, not Quill. Flutter authors this through a Quill editor and stores a delta in `notes`
 * with the flattened text in `notePlainText`. We write our plain text to BOTH and READ
 * `notePlainText` first, so a Flutter-authored delta renders as prose here instead of raw JSON —
 * the same rule A/P-5 applies to order notes. A real rich-text editor is a separate, larger question.
 *
 * Per the ap-empty anchor the block only renders once the canvas has content (no ADDED NOTES on an
 * empty A&P) — or whenever a note already exists, so a saved note is never hidden.
 */
function ApAddedNotes({
  value,
  onEdit,
  onClear,
  placeholder,
  readOnly,
}: {
  value: string;
  /** Open the "Add Text Notes" dialog — the ONE editor (Flutter's commentButton). */
  onEdit?: () => void;
  /** Clear the narrative (the red trash on the card). */
  onClear?: () => void;
  placeholder: string;
  readOnly?: boolean;
}) {
  const live = !readOnly && !!onEdit;
  const designV2 = useDesignV2();

  // The inline textarea this used to open is GONE (2026-08-05). Authoring happens in one place —
  // the rich-text dialog — because the note is a Quill Delta on the wire and a plain textarea
  // could only ever write text, silently flattening any formatting a doctor added in Flutter.
  return (
    // Full width of the canvas column (Andrew, 2026-08-05, against the live A/P frame). It used
    // to cap at 584px, which left the card floating short of the order rows beneath it.
    <div data-testid="added-notes" className="flex flex-col gap-2">
      <span className={designV2 ? SECTION_HEADING_12_CLASS_V2 : SECTION_HEADING_12_CLASS}>Added Notes</span>
      {/* E1 design (EncounterScreen.dc.html:648-656): a BORDERED card (hairline
          --color-border-strong, radius-md) rather than the shadow card this used to draw, 13px
          body at 1.6, and the two 24px affordances at the TOP of the card (align-items:
          flex-start) so a multi-paragraph note doesn't push them to its vertical centre. */}
      <div className={designV2 ? ADDED_NOTES_CARD_CLASS_V2 : ADDED_NOTES_CARD_CLASS}>
        <p
          className={cn(
            'flex-1 min-w-0 m-0 text-size-sm leading-reading whitespace-pre-wrap',
            // R9 — the note body renders through `QuillStaticView`, whose text run is
            // `normalLabelTextStyle(15, ...)` at the 1.4 default (`quill_static_view.dart:46`).
            value.trim() ? 'text-text-primary' : 'text-text-hint',
          )}
        >
          {value.trim() ? value : placeholder}
        </p>
        <button
          type="button"
          disabled={!live}
          onClick={live ? onEdit : undefined}
          aria-label={live ? 'Edit notes' : 'Edit notes (read-only)'}
          className={cn(
            'icon-action',
            live ? 'opacity-100 cursor-pointer' : 'opacity-45 cursor-not-allowed',
          )}
        >
          <Pencil className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          disabled={!live || !value.trim() || !onClear}
          onClick={live && onClear ? onClear : undefined}
          aria-label={live ? 'Delete notes' : 'Delete notes (read-only)'}
          className={cn(
            designV2 ? NOTES_DELETE_BTN_CLASS_V2 : NOTES_DELETE_BTN_CLASS,
            live && value.trim() ? 'opacity-100 cursor-pointer' : 'opacity-45 cursor-not-allowed',
          )}
        >
          <Trash2 className="size-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** An empty Quill document — Delta requires a trailing newline. Clearing the note must write
 *  THIS into `notes`, not an empty string: a bare '' is not a parseable Delta for the Flutter
 *  reader (src/dashboard/quill-delta.ts). */
const EMPTY_NOTE_DELTA = JSON.stringify([{ insert: String.fromCharCode(10) }]);

// ── Order sections (ROUND-4 region 5) ────────────────────────────────────────────────────────────
// Flutter's A&P canvas groups the plan's orders per TYPE under a Section Heading 12 group label
// (same label role as DIAGNOSES/ADDED NOTES). Rows render through the DS ApOrderCard — the
// Figma-anchored order block (Bars/New/Assessment Add On, canonical ORDER_IDENTITY left accent).
/**
 * Order run order on the canvas. The `label` is NOT rendered any more (see the render site —
 * the canvas carries one visible label, DIAGNOSES); it survives as the run's `aria-label`.
 *
 * Sequence is the live A/P frame's, read off it directly (2026-08-05): Labs · Medications ·
 * Radiology · Vaccination · Procedure, Referral last. That differs from `uiOrders()` in
 * `features/common/ui_data.dart` (which puts Vaccination before Radiology) — but `uiOrders()`
 * feeds the NOTE's Orders tab (`orders_tab.dart`), not this canvas, so the frame wins here.
 */
const ORDER_GROUPS: { type: DsOrderType; label: string }[] = [
  { type: 'lab', label: 'Labs' },
  { type: 'medication', label: 'Medications' },
  { type: 'imaging', label: 'Imaging' },
  { type: 'vaccination', label: 'Vaccinations' },
  { type: 'procedure', label: 'Procedures' },
  { type: 'referral', label: 'Referrals' },
];

/**
 * A/P HEADER ACTION ROW — the E1 design's own set (EncounterScreen.dc.html:545-557):
 * search · edit · comment · the [I] Scrips-Intelligence mark · note.
 *
 * NOT the shared `SECTION_ACTIONS` (mic · edit · message · sparkle) the questionnaire sections
 * use: A/P's header in the design carries a plan SEARCH and a NOTE button where those carry
 * dictation, and reproducing the questionnaire row here would be the wrong header for this
 * section. `sparkle` stands in for the design's `[I]` avatar — that mark IS Scrips Intelligence,
 * and the DS already ships it as an icon action, so it is composed rather than hand-drawn.
 *
 * ANTI-FICTION: every one of these is visible-but-DISABLED except `message`, which is the only
 * one with a real behaviour behind it (Flutter's `commentButton` → the assessment-note dialog).
 */
/** No payloads yet: the Save-as list then offers no orders. */
const EMPTY_PAYLOADS: OrderPayloadMap = new Map();

// Each action's Lucide glyph (icon pass, 16 Oct): the concept's glyph from the app's one table. `sparkle` is the
// slot Add Order dispatches on; it draws Plus in both modes (sparkle is reserved for Scrips Intelligence).
const AP_SECTION_ACTIONS = [
  { name: 'search', glyph: Search, label: 'Search the plan (coming soon)' },
  { name: 'edit', glyph: Pencil, label: 'Edit section (coming soon)' },
  { name: 'message', glyph: MessageSquare, label: 'Comment on section (coming soon)' },
  { name: 'sparkle', glyph: Plus, label: 'Scrips Intelligence (coming soon)' },
] satisfies { name: string; glyph: LucideIcon; label: string }[];

/** Host-composed ApOrderCard detail line from the structured payload (side-map). */
function orderDetail(payload: CodedOrderPayload | undefined): string | undefined {
  if (!payload) return undefined;
  if (payload.kind === 'medication') {
    const parts = [
      payload.sig,
      payload.refill != null ? `Refills: ${payload.refill}` : undefined,
      payload.dispense != null ? `Dispense: ${payload.dispense}${payload.dispenseUnit ? ` ${payload.dispenseUnit}` : ''}` : undefined,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : undefined;
  }
  // PROD-2640: the design's lab detail line (specimen · n tests · ordered as one panel).
  if (payload.kind === 'lab') return labPlanDetail(payload);
  if ('note' in payload && payload.note) return payload.note;
  return undefined;
}

/** `?x=prescription-card`: the card's schedule as the catalog builds it (frequency · times a day · units · route), from the saved dose. */
function medicationSchedule(
  med: Extract<CodedOrderPayload, { kind: 'medication' }> | undefined,
  /** `?x=rx-sig-wording`: an every-day order says its times a day once ("Once a day"), not "Every day · 1 time a day". */
  sigWording = false,
): string | undefined {
  if (!med) return undefined;
  const everyDayCount = sigWording && med.frequency === MED_FREQUENCY.everyDay && !!med.timesADay;
  const frequency = everyDayCount ? undefined : (MED_FREQUENCY_OPTIONS.find((f) => f.value === med.frequency)?.label ?? med.frequency);
  const timesADay = !med.timesADay
    ? undefined
    : everyDayCount && med.timesADay === 1
      ? 'Once a day'
      : `${med.timesADay} ${med.timesADay === 1 ? 'time' : 'times'} a day`;
  const parts = [
    frequency,
    timesADay,
    med.quantity != null ? [med.quantity, med.doseUnit].filter((x) => x != null && x !== '').join(' ') : undefined,
    med.route?.displayName,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

/** Coding-system code shown after the order name (LOINC/CPT/CVX…), from the payload. */
function orderCode(payload: CodedOrderPayload | undefined): string | undefined {
  if (!payload) return undefined;
  if (payload.kind === 'medication') return payload.medicationCode.code;
  if (payload.kind === 'vaccination') return payload.entry.vaccine?.code ?? undefined;
  if (payload.kind === 'referral') return undefined;
  if ('code' in payload) return payload.code.code;
  return undefined;
}

/** TS-3 · an A or P part with no diagnosis yet: one line saying where diagnoses come from. It stays the
 *  drop target the empty canvas was — an item dragged from the search panel is added here as it is on
 *  the canvas (no diagnosis to land on, so the host decides: a diagnosis is added, an order is held). */
function ApEmptyDropLine({
  slot,
  text,
  onDropItem,
  solidOutline = false,
}: {
  slot: string;
  text: string;
  onDropItem?: (payload: ApDragPayload) => void;
  /** PROD-3398 (`diagnosesBlock`): the drop outline is solid, as the catalog's `.empty[data-drop-active]` (@62cb3650). */
  solidOutline?: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <p
      data-slot={slot}
      data-drop-active={over ? 'true' : undefined}
      onDragOver={(e) => {
        if (onDropItem && hasApDragData(e)) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!onDropItem || !hasApDragData(e)) return;
        e.preventDefault();
        setOver(false);
        const payload = readApDragData(e);
        if (payload) onDropItem(payload);
      }}
      className={cn(
        'm-0 rounded-md font-sans text-size-sm text-text-secondary',
        over && cn('outline outline-2 outline-brand-primary outline-offset-2', solidOutline ? 'outline-solid' : 'outline-dashed'),
      )}
    >
      {text}
    </p>
  );
}

/** TS-3 · the A or P part of the A/P inside the note: the part's actions on their own row, and no title —
 *  the note's section and block name it (design desk, 5 Oct: each title once). */
function ApPartFrame({ label, actions, children, dropEmptyToolbar = false }: { label: string; actions: ReactNode; children: ReactNode; dropEmptyToolbar?: boolean }) {
  // PROD-2947 (6 Oct contract L9): inside the note a part with no live action draws NO toolbar row — after lane D moved
  // the narrative's note action into the block header, Diagnoses kept an empty 16px row above its add line.
  const empty = Array.isArray(actions) ? actions.length === 0 : actions == null || actions === false;
  return (
    <section data-testid="ap-section-chrome" aria-label={label} className="flex flex-col gap-4 font-sans">
      {!(dropEmptyToolbar && empty) && (
        <div role="toolbar" aria-label={`${label} actions`} className="flex flex-row items-center justify-end gap-2">
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function AssessmentPlanView({
  diagnoses,
  orders,
  allowedOrderTypes,
  roleLabel,
  readOnly,
  notice,
  providerId,
  bundles,
  bundlesLoading,
  onImportBundle,
  onDeleteBundle,
  saveAsOrderSet,
  onOpenAddOrder,
  onAddToFavourites,
  seedRequest,
  onSeedConsumed,
  onHoldOrder,
  pendingSeedCount = 0,
  deletingBundle,
  patientId,
  gender,
  onReorder,
  onSetPrimary,
  onLinkOrderDx,
  onAddOrder,
  onRemoveOrder,
  onRemoveDx,
  onAddDx,
  onAttachOrder,
  assessmentNote = '',
  assessmentNoteDelta,
  onAssessmentNoteChange,
  noteChartReads,
  noteMacroScope,
  noteMacros,
  noteSuggested,
  dxCoding,
  aiDiagnosisEnabled,
  orderPayloads,
  onUpdateOrder,
  medicationResults,
  medicationQuery,
  onMedicationQueryChange,
  onAttachMedication,
  articles,
  onAddArticle,
  onRemoveArticle,
  patientProblems,
  showProblemListDisclosure = false,
  keptOffDxIds,
  resolvedNowDxIds,
  onKeepOffDx,
  onResolveDxWithVisit,
  dxReceipt,
  activeMedications,
  onChangeMedication,
  onRefillMedication,
  onDiscontinueMedication,
  onDeleteMedication,
  orderSets,
  visitComplaint,
  part,
  inNote,
}: AssessmentPlanViewProps) {
  // TS-3: which part draws. `drawsDx` = the diagnoses (whole A/P or A); `drawsOrders` = the orders and
  // everything after them (whole A/P or P).
  const drawsDx = part !== 'plan';
  const drawsOrders = part !== 'assessment';
  // The picker open-state (null = closed) + which Dx/type it's for. The only local UI state.
  const [open, setOpen] = useState<OpenPicker | null>(null);
  // Design sheet v1.1, switch ON only (`?ef=frameV2`); OFF renders today's classes and glyphs.
  const designV2 = useDesignV2();
  // [PROD-3206] `?x=assign-care-plan`: the physician's "Assign care plan" action in the Plan area. OFF by default;
  // OFF renders today's DOM and makes no care-model call. Read once per mount, like the frame switch.
  const [assignCarePlanOn] = useState(() => isEncounterFlagOn('assignCarePlan'));
  // `?x=slash-insert`: the backslash add line above the Orders add line. OFF by default; read once per mount.
  const [slashInsertOn] = useState(() => isEncounterFlagOn('slashInsert'));
  // PROD-3399 `?x=one-order-line` (ON by default on the dev hosts): with `slashInsert`, the Orders block's one "Add order" line takes
  // the backslash triggers; `?x=-one-order-line` brings back the separate "Add with \" line. Read once per mount.
  const [oneOrderLineOn] = useState(() => isEncounterFlagOn('oneOrderLine'));
  // PROD-3398 `?x=diagnoses-block` (ON by default on the dev hosts): the catalog's Diagnoses block (card 29a). Empty, the block is
  // the Add diagnosis line alone; an empty part's drop outline is solid. `?x=-diagnoses-block` is today's. Read once per mount.
  const [diagnosesBlockOn] = useState(() => isEncounterFlagOn('diagnosesBlock'));
  // Item 6 (4 Nov): the diagnosis row draws its bin after the card, not on the role control.
  const [dxBinInlineOn] = useState(() => isEncounterFlagOn('dxBinInline'));
  // `?x=dx-order-split` (Andrew, 4 Nov; ON by default on the dev hosts): the order lines list order items only; the Diagnoses line
  // takes \fav \sug \set. Read once per mount.
  const [dxOrderSplitOn] = useState(() => isEncounterFlagOn('dxOrderSplit'));
  const complaintText = visitComplaint?.kind === 'ready' ? visitComplaint.complaints.join(', ') : undefined;
  // PROD-3423 `?x=explicit-dx-link` (ON by default on the dev hosts): no diagnosis pre-selected unasked when there are 2+.
  const [explicitDxLinkOn] = useState(() => isEncounterFlagOn('explicitDxLink'));
  // `?x=prescription-card`: the Plan's medication orders as prescription cards after the Plan (prescription-cards.tsx). ON by
  // default on the dev hosts only. Same orders, dialogs and save path; read once per mount.
  const [prescriptionCardOn] = useState(() => isEncounterFlagOn('prescriptionCard'));
  // `?x=rx-sig-wording` (ON by default on the dev hosts): "Once a day", not "Every day · 1 time a day". Read once per mount.
  const [rxSigWordingOn] = useState(() => isEncounterFlagOn('rxSigWording'));
  const rxCards = prescriptionCardOn && part === 'plan';
  /**
   * PROD-3041 · ADD-LINE MODE (frame v2, the A&P part drawn inside the note). Live dev review, 6 Oct
   * (F2/F3): the legacy search rail inside the Diagnoses and Orders blocks (a blue "Dx" bar, text tabs,
   * a "Global" list, match scores as bars, a second search entry point) is replaced by ONE "Add
   * diagnosis" / "Add order" line at the top of the block (design-desk ruling D-75 and its Orders
   * amendment; Samer lifted the freeze at about 19:05 the same evening). Diagnoses: the cards in a
   * 240px+ grid of up to 4 columns. Orders: the order rows as today. Every pick goes through the
   * handlers below, so the save path and every order dialog are unchanged. Scrips Intelligence
   * suggestions moved to the right rail. Outside the note, and with the switch OFF, the rail is
   * exactly as before.
   */
  const addLineMode = designV2 && !!inNote && !!part;
  // M2b: which Dx card's Primary/Secondary picker is open — the HOST owns the one-primary policy
  // popup (EncounterDxCard fires onRoleClick only; ApDiagnosisCard's built-in dropdown retired
  // with the card, four-moments wave). ModalDropdown = the DS priority picker (C3: menu).
  const [rolePickerDxId, setRolePickerDxId] = useState<string | null>(null);
  // Spoken form of the one-primary invariant (see the dropdown's onChange).
  const [priorityNotice, setPriorityNotice] = useState<string | null>(null);
  /**
   * The assessment-narrative POPUP (Andrew, 2026-08-05). Flutter authors the A/P narrative in a
   * dialog off the section header's comment button, not in an always-on inline card; the inline
   * ADDED NOTES block below is the read-back of a SAVED note and renders only when one exists.
   * Draft is local so Cancel discards — the host only sees the text on Save.
   */
  const [noteOpen, setNoteOpen] = useState(false);
  /**
   * WHICH ORDER'S DIAGNOSIS LINKS ARE BEING EDITED (null = closed).
   *
   * The E1 design draws an order's linked diagnoses as PLAIN MONO TEXT (:712-716). What it
   * replaces — the DS `ApOrderCard`'s `DxLinkChip` row — was interactive: each chip toggled
   * whether this order justified that diagnosis. Shipping the static text alone would have
   * deleted a real clinical capability (re-linking an order to a different problem) to win a
   * pixel match, so the design's appearance is kept AND the capability is kept: the whole code
   * group is one operable target that opens this editor, which is the same
   * `DiagnosisMultiSelect` block every order dialog already carries, writing through the same
   * `onLinkOrderDx`. Nothing about the wire contract changes.
   */
  const [linkEditorOrderId, setLinkEditorOrderId] = useState<string | null>(null);
  /** Draft link set while the editor is open — Cancel discards, Save writes once. */
  const [linkDraft, setLinkDraft] = useState<string[]>([]);
  // Monotonic suffix so repeated attaches (same code, >1 Dx) get distinct stable orderIds
  // without a Math.random/Date.now source.
  const [orderSeq, setOrderSeq] = useState(0);
  // PROD-1167: the medication picker is a 2-step flow — search → pick → capture a
  // structured dose. `pickedMed` holds the chosen medication while the dose dialog is open.
  // Typed as MedicationPick so BOTH the search-widget MedicationResult and a dragged
  // MedicationOption (seeded, PROD-1419) are assignable — the dose dialog takes MedicationPick.
  const [pickedMed, setPickedMed] = useState<MedicationPick | null>(null);

  // The full working AP medication list (Flutter `apData.medications()`). The dose dialog prepends
  // the med being entered and sends the whole set to the interaction engine, so drug-drug pairs
  // WITHIN the draft plan are detected — matching Flutter `getDrugInteractionInfo`, which appends
  // the picked item to `apData.medications()` rather than checking the single new med in isolation.
  const existingMedications = useMemo<DrugInteractionMedInput[]>(() => {
    const out: DrugInteractionMedInput[] = [];
    for (const o of orders) {
      if (o.type !== 'medication') continue;
      const payload = orderPayloads?.get(o.id);
      if (payload?.kind !== 'medication') continue;
      const code = payload.medicationCode?.code;
      if (!code) continue;
      out.push({
        code,
        display: payload.medicationCode.displayName,
        system: payload.medicationCode.system,
        quantity: payload.quantity != null ? payload.quantity : '1',
      });
    }
    return out;
  }, [orders, orderPayloads]);

  // PROD-2640: the labs already on this plan (their own code + selected members), so the lab popup
  // can say "Already ordered in <panel>; drawn once". The order being edited is not "another" lab.
  const editingLabId = open?.type === 'lab' ? open.editingOrderId : undefined;
  const otherLabs = useMemo(
    () => otherLabsOnPlan(orders, orderPayloads, editingLabId),
    [orders, orderPayloads, editingLabId],
  );

  // FOUR-MOMENTS RETIREMENT (spec §10.8, mounting wave): the flat patient-blind diagnosis search is
  /**
   * DIAGNOSIS IS BACK, and FIRST (Andrew, 31 Jul: "first diagnosis tab missing in the left panel").
   *
   * It had been retired here with a comment citing "spec §10.8" — a document that does NOT exist in
   * this repo, and the divergence was never entered in CLAUDE.md's register either. The left-panel
   * review flagged exactly that: an undocumented divergence reads as drift to the next reviewer, and
   * a strip starting at Labs reads as a regression to anyone comparing against Flutter, whose first
   * tab is diagnosis (app_enum.dart:51-61, strip at
   * assessment_plan_search_list_widget.dart:111-121).
   *
   * The patient-aware Dx picker above the panel STAYS — it does something the panel cannot (groups
   * results against the patient's existing problem list, the four-moments work). Flutter offers both
   * too: the panel tab AND the AI/suggested section. Two ways in, not a duplicate.
   *
   * Only the tab is restored here; the search behind it already existed
   * (use-ap-category-search.ts handles 'diagnosis' via useDiagnosisCodeSearch).
   */
  // TS-3: A searches diagnoses only; P keeps every category (a diagnosis an order needs can be added
  // without leaving P — the held-order banner's "Search diagnoses" — and lands in A) but opens on the
  // first order type.
  const categories: ApSearchCategory[] =
    part === 'assessment'
      ? ['diagnosis']
      : ['diagnosis', ...allowedOrderTypes, ...(onAddArticle ? (['education'] as ApSearchCategory[]) : [])];
  const planRailSeed: { category: ApSearchCategory; query: string } | undefined =
    part === 'plan' ? { category: categories.find((c) => c !== 'diagnosis') ?? 'diagnosis', query: '' } : undefined;
  const canEditNote = !readOnly && !!onAssessmentNoteChange;
  // Transient guidance when an order is dropped/activated with no diagnosis to attach it to.
  const [dropHint, setDropHint] = useState<string | null>(null);

  /**
   * HOLD, DON'T DROP (`frameV2`, Samer 1 Oct: "orders are not add").
   *
   * An order sits under a diagnosis; that is the approved model and it does not change here. What
   * changes is what happens to the clinician's click when the plan has no diagnosis yet. Every add
   * path (the Add-Order drawer's ＋, the ambient "Find in catalogue…" / "Add order…", this rail's ＋
   * and a drop) ended in the same place: the pick was thrown away and a one-line hint said "Add a
   * diagnosis first". So she pressed ＋, the screen changed step, and nothing was added.
   *
   * With the switch ON the pick is handed to the host as the pending seed (it survives a step
   * change), the line below names it and says what it needs, and its form opens once a diagnosis
   * exists. Opening the form writes nothing: she still completes it, and a completed form PENDS
   * the order. Nothing here widens who may order; the categories come from the host's permissions.
   */
  const holdsOrders = designV2;
  function holdForDiagnosis(category: DsOrderType, item: unknown): boolean {
    if (!holdsOrders || !onHoldOrder) return false;
    setDropHint(null);
    onHoldOrder({ item, category });
    return true;
  }
  /**
   * "Search diagnoses" on the held line re-opens the rail on its Diagnosis tab. The rail seeds its
   * tab from a `useState` initialiser, so a new `key` is what makes the seed apply. Both are
   * constant until that button is pressed, which only exists with the switch ON.
   */
  const [railKey, setRailKey] = useState(0);
  const [railSeed, setRailSeed] = useState<{ category: ApSearchCategory; query: string } | undefined>();

  const medPickerEnabled = !!onMedicationQueryChange;

  // Drop-anywhere target: the card an order was dropped on, else the primary (or first) Dx. Flutter's
  // addItemInApSection attaches to `assessmentPlanCondition` (the selected/primary Dx) — the drop need
  // not land on a specific card (assessment_plan_screen.dart:130).
  const defaultDxId = (diagnoses.find((d) => d.isPrimary) ?? diagnoses[0])?.id;
  /** The drawer pick waiting for a diagnosis (`frameV2` only); see `holdForDiagnosis`. */
  const heldSeed = holdsOrders && seedRequest && !defaultDxId ? seedRequest : null;

  // Open the matching picker for `type`, SEEDED with the dragged `item` (PROD-1419). Medication skips
  // the search widget and opens the dose editor directly with the dragged option; the four coded
  // dialogs open with `initialItem` pre-selected. Anything we can't open (no picker capability wired)
  // falls through to the host's v1 placeholder onAddOrder so nothing crashes.
  //
  // GATES ARE FOR THE SEEDED PATH (Codex Medium, PROD-1419): a SEEDED drop preselects the dragged item,
  // so the dialog does NOT need the panel's search capability — the med dose editor needs only
  // `onAttachOrder` (NOT `onMedicationQueryChange`), and a coded dialog needs only `onAttachOrder`
  // (NOT `providerId` — that just scopes the dialog's own search, which is hidden when seeded). The
  // search-callback / providerId gates only apply to a NON-seeded open (no item), which v2 never does.
  function openSeededPicker(dxId: string, type: DsOrderType, item: unknown, dictatedDose?: MedicationDose, dxDefaulted = false) {
    const defaulted = dxDefaulted ? { dxDefaulted: true } : {};
    if (type === 'medication') {
      // A dragged medication is NEVER re-searched. With `onAttachOrder`, go straight to the dose editor
      // seeded with the dragged option (MedicationOption is assignable to MedicationPick). Without it
      // (legacy `onAttachMedication`-only host), attach the dragged med directly. Only a no-item open
      // ("+ Med" button) falls back to the search widget (needs `onMedicationQueryChange`).
      if (item && onAttachOrder) {
        setOpen({ dxId, type, ...defaulted, ...(dictatedDose ? { dictatedDose } : {}) });
        setPickedMed(toMedicationPick(item));
        return;
      }
      if (item && onAttachMedication) {
        onAttachMedication(dxId, item as MedicationResult);
        return;
      }
      if (medPickerEnabled) {
        setOpen({ dxId, type, ...defaulted });
        return;
      }
      onAddOrder?.(dxId, type);
      return;
    }
    // Coded order (lab/imaging/procedure/referral/vaccination): a seeded drop preselects `item`, so
    // open the dialog whenever `onAttachOrder` is wired — no `providerId` required.
    if (onAttachOrder) {
      setOpen({ dxId, type, ...defaulted, initialItem: item });
      return;
    }
    onAddOrder?.(dxId, type);
  }

  // Panel → canvas drop. A diagnosis (dropped anywhere) adds a Dx; an order opens the matching picker
  // SEEDED with the dragged item, attached to the dropped-on card OR the primary/first Dx (drop
  // anywhere — Flutter addItemInApSection). Only when there is NO diagnosis at all do we surface the
  // "add a diagnosis first" hint (Flutter's "Add diagnosis first").
  function handleDrop(payload: ApDragPayload, targetDxId?: string) {
    // An ORDER SET dragged from the rail (PROD-2712) rides the A/P drag with a marked item — checked
    // first so it is never read as an order. Dropped on a diagnosis → applied to it; dropped
    // anywhere else → the same as its ＋ (the set's saved diagnosis is suggested).
    if (orderSets && isOrderSetDragItem(payload.item)) {
      setDropHint(null);
      void orderSets.apply(payload.item.id, targetDxId);
      return;
    }
    if (payload.kind === 'diagnosis') {
      setDropHint(null);
      onAddDx?.(payload.item as CodeSearchResult);
      return;
    }
    const target = targetDxId ?? defaultDxId;
    if (!target) {
      if (holdForDiagnosis(payload.kind, payload.item)) return;
      setDropHint('Add a diagnosis first, then drag the order onto the canvas.');
      return;
    }
    setDropHint(null);
    // A drop ON a diagnosis card is the clinician's choice; anywhere else the primary is a fallback (PROD-3423).
    openSeededPicker(target, payload.kind, payload.item, undefined, !targetDxId);
  }

  // Panel click / keyboard activation (the a11y path parallel to drag). Diagnosis adds directly; an
  // order auto-targets the primary/first Dx and opens SEEDED — else the no-diagnosis hint.
  function handleActivate(item: ApSearchItem, category: ApSearchCategory) {
    if (category === 'diagnosis') {
      setDropHint(null);
      onAddDx?.(item.item as CodeSearchResult);
      return;
    }
    // Education isn't an order — its add path lives in the rail's education tab (ApSearchRail),
    // never this order-activation path. Guard both for safety and to narrow the type to OrderType.
    if (category === 'education') return;
    if (!defaultDxId) {
      if (holdForDiagnosis(category, item.item)) return;
      setDropHint('Add a diagnosis first, then add the order.');
      return;
    }
    setDropHint(null);
    openSeededPicker(defaultDxId, category, item.item, undefined, true);
  }

  /** PROD-3041: a pick from the block's add line, routed to the SAME handlers the rail's ＋ used. */
  function handleAddLinePick(pick: AddLinePick): Promise<void> | void {
    if (pick.kind === 'item') {
      handleActivate(pick.item, pick.category);
      return;
    }
    if (pick.kind === 'order-set') {
      setDropHint(null);
      // PROD-3147: the line says "Applying the order set …" until this settles, then clears it (the receipt or the
      // failure notice announces the outcome).
      return orderSets?.apply(pick.set.id);
    }
    if (pick.kind === 'bundle') {
      onImportBundle?.(pick.bundle);
      return;
    }
    onAddArticle?.(pick.article);
  }
  /** `<type>:<CODE>` of the orders already on the plan (the Orders line shows such a row as added). */
  const existingOrderKeys = orders.flatMap((o) => {
    const code = orderCode(orderPayloads?.get(o.id))?.trim();
    return code ? [`${o.type}:${code.toUpperCase()}`] : [];
  });
  /** Order sets (flag on, a user who may order) or, with them off, the bundles an import exists for. */
  const addLineSetSources = orderSets
    ? { orderSets: { visitCodes: diagnoses.map((d) => d.code ?? '').filter(Boolean) } }
    : { bundles, canImportBundle: !!onImportBundle };

  // Drain a drawer pick exactly once. Effect (not render) because it opens a dialog and clears the
  // request — both state writes, which a render-time call would loop on.
  //
  // `frameV2`: with no diagnosis the pick is KEPT (not consumed) and the held line below says what
  // it needs, so the effect also re-runs when a diagnosis appears (`heldDxId`) and when an open form
  // closes (`heldBusy`) — it never opens a second form over one the clinician is filling in. With
  // the switch OFF both are constant, so the effect runs on `seedRequest` alone, exactly as before.
  const heldDxId = holdsOrders ? defaultDxId : undefined;
  const heldBusy = holdsOrders ? !!open : false;
  useEffect(() => {
    // TS-3: only the part that draws the orders finishes a handed-over order (one scroll mounts both).
    if (!drawsOrders) return;
    if (!seedRequest) return;
    if (!defaultDxId) {
      if (holdsOrders) {
        setDropHint(null);
        return;
      }
      setDropHint('Add a diagnosis first, then add the order.');
      onSeedConsumed?.();
      return;
    }
    if (heldBusy) return;
    setDropHint(null);
    openSeededPicker(defaultDxId, seedRequest.category, seedRequest.item, seedRequest.dictatedDose, true);
    onSeedConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedRequest, heldDxId, heldBusy]);

  function closePicker() {
    setOpen(null);
    setPickedMed(null);
    if (open?.type === 'medication') onMedicationQueryChange?.('');
  }

  // A dialog confirmed. Editing an existing order → UPDATE it in place (host keeps the id; the
  // form's diagnosis selection re-links it, PROD-2886); a fresh attach → append + bump the id suffix. Either way, close + clear the picked med (else the
  // next "+ Med" open would jump straight into the dose dialog with the STALE med — Codex CRITICAL).
  // The DIAGNOSIS (n) * options every order dialog renders — the canvas's own Dx cards, reduced to
  // what the block needs (PROD-1801).
  const dxOptions = diagnoses.map((d) => ({ id: d.id, code: d.code, display: d.display }));

  // Editing an existing order: seed the multi-select from its FULL dxIds, not just the parent. Seeding
  // only dxIds[0] would silently drop every other link the moment the clinician saved — re-creating
  // exactly the defect #269 fixed in the save mapper.
  const editingOrderDxIds = open?.editingOrderId
    ? orders.find((o) => o.id === open.editingOrderId)?.dxIds
    : undefined;
  /** The dialog's pre-selected diagnoses. An edit keeps its links. PROD-3423 (`explicitDxLink`): when the primary was only a fallback
   *  and the dialog offers 2+ diagnoses (`dxOptions`), none is pre-selected, so the dialog's required DIAGNOSIS block asks; otherwise the opened one. */
  const initialDxFor = (dxId: string): readonly string[] =>
    editingOrderDxIds ?? (explicitDxLinkOn && open?.dxDefaulted && dxOptions.length > 1 ? [] : [dxId]);

  // The order dialogs' destructive DELETE (the red trash both Figma frames put bottom-right). It is a
  // second route to the removal the order ROW already offers, so it reuses `onRemoveOrder` rather than
  // introducing a parallel delete path — and it only exists while EDITING an existing order (there is
  // nothing to delete during an add), which is why this is undefined otherwise and the glyph vanishes.
  const editingOrderId = open?.editingOrderId;
  const deleteEditingOrder =
    editingOrderId && onRemoveOrder && !readOnly
      ? () => {
          onRemoveOrder(editingOrderId);
          closePicker();
        }
      : undefined;

  function handleAttach(dxIds: string[], planOrder: PlanOrder, payload: CodedOrderPayload) {
    if (open?.editingOrderId) {
      // PROD-2886: the edit form's DIAGNOSIS (n) * selection re-links the order (Flutter
      // updateItemDiagnosis), the same write as the link editor; the host keeps the links when it is empty.
      onUpdateOrder?.(open.editingOrderId, planOrder, payload, dxIds);
    } else {
      onAttachOrder?.(dxIds, planOrder, payload);
      setOrderSeq((n) => n + 1);
    }
    setOpen(null);
    setPickedMed(null);
  }

  // Click-to-edit a listed order (PROD-1433): reopen the matching dialog SEEDED from the saved payload
  // (Flutter's `originalData`), tagged with editingOrderId so confirm updates in place. Reconstruct the
  // dialog's seed per kind from the side-map payload.
  function handleEditOrder(orderId: string) {
    const order = orders.find((o) => o.id === orderId);
    const payload = orderPayloads?.get(orderId);
    if (!order || !payload) return;
    const dxId = order.dxIds[0] ?? defaultDxId;
    if (!dxId) return;
    setDropHint(null);
    if (payload.kind === 'medication') {
      setOpen({ dxId, type: 'medication', editingOrderId: orderId, editPayload: payload });
      setPickedMed(payloadToMedicationPick(payload));
      return;
    }
    if (payload.kind === 'vaccination') {
      setOpen({ dxId, type: 'vaccination', editingOrderId: orderId, editPayload: payload, initialItem: vaccinationEntryToCoding(payload.entry) });
      return;
    }
    if (payload.kind === 'procedure') {
      // Carry the stored coding system ('cpt' | 'snomed') on the seed so the dialog's system toggle matches.
      const item = { ...payloadToCodeSearchResult(payload)!, system: payload.system };
      setOpen({ dxId, type: 'procedure', editingOrderId: orderId, editPayload: payload, initialItem: item });
      return;
    }
    // lab / imaging / referral — referral has no coded item (initialItem stays undefined).
    setOpen({ dxId, type: payload.kind, editingOrderId: orderId, editPayload: payload, initialItem: payloadToCodeSearchResult(payload) });
  }

  // PROD-2723: with order sets on, the orders nest under their diagnosis (board 6). Off → null, and
  // the orders region is today's flat list, untouched.
  // PROD-2850: an article a set attached sits in that diagnosis card; the Education section keeps
  // the rest (hand-added, or waiting for a diagnosis).
  const orderSetGrouping = orderSets
    ? groupOrdersByDiagnosis(diagnoses, orders, orderSets.applications, articles ?? [], orderSets.articleLinks)
    : null;
  // TS-3 · P: the orders ALWAYS nest under the first diagnosis they justify (R6c Plan: "orders, each
  // under its diagnosis"), order sets on or off. A draws the diagnoses alone, never their orders.
  const planGrouping =
    part === 'plan'
      ? groupOrdersByDiagnosis(diagnoses, orders, orderSets?.applications ?? [], articles ?? [], orderSets?.articleLinks ?? [])
      : null;
  const canvasGrouping = part === 'assessment' ? null : orderSetGrouping;
  const sectionArticles = (articles ?? [])
    .map((article, index) => ({ article, index }))
    .filter(({ index }) => !(planGrouping ?? orderSetGrouping)?.nestedArticleIndexes.has(index));
  // The section numbers its own list; a remove by position maps back to the A/P's article list.
  // Both are derived in THIS render from the same `articles` prop (never stored), so the position
  // the section reports and the index it maps to come from one snapshot. A saved article removes by
  // its id. Flag off (no `orderSets`): nothing is nested and the mapping is the identity.
  /** The grouping the order list reads its unlinked orders from: P's, else the order-set one. */
  const listGrouping = part === 'plan' ? planGrouping : orderSetGrouping;
  const removeSectionArticle = onRemoveArticle
    ? (idOrIndex: string | number) =>
        onRemoveArticle(typeof idOrIndex === 'number' ? (sectionArticles[idOrIndex]?.index ?? idOrIndex) : idOrIndex)
    : undefined;
  /** The canvas EDUCATION section, or nothing when there is no article to list. */
  const educationSection =
    sectionArticles.length > 0 ? (
      <PatientEducationSection
        // PROD-3147: a summary that is only the library's result type ("problems") is no description.
        articles={sectionArticles.map(({ article: a }) => ({ id: a.id, title: a.display, description: articleBlurb(a.summary) }))}
        readOnly={readOnly}
        onRemove={removeSectionArticle}
      />
    ) : undefined;

  /**
   * dxIds → the CODES the row prints, in link order. Falls back to the diagnosis display when a
   * free-text Dx has no code (better a term than a blank slot), and skips ids the canvas no longer
   * holds so a stale link can never print as an empty run of mono text.
   */
  function dxCodesFor(dxIds: readonly string[]): string[] {
    const out: string[] = [];
    for (const id of dxIds) {
      const dx = diagnoses.find((d) => d.id === id);
      if (!dx) continue;
      out.push(dx.code || dx.display);
    }
    return out;
  }

  /**
   * The order rows for a run of orders, same-type orders together in ORDER_GROUPS order. The flat
   * list (flag off) passes every order; the per-diagnosis groups (PROD-2723, order sets on) pass
   * each diagnosis's own, so a nested row is exactly the row the flat list draws.
   */
  function renderOrderRuns(list: readonly PlanOrder[]): ReactNode {
    return ORDER_GROUPS.map((g) => {
      // `?x=prescription-card`: the medications are the Prescriptions cards after the Plan, not rows here.
      if (rxCards && g.type === 'medication') return null;
      const rows = list.filter((o) => o.type === g.type);
      if (rows.length === 0) return null;
      return (
        <section key={g.type} aria-label={g.label} className="flex flex-col gap-2">
          {rows.map((o) => {
            const payload = orderPayloads?.get(o.id);
            const med = payload?.kind === 'medication' ? payload : undefined;
            // Flutter's `additionalWidget` on a medication row (assessment_plan_screen.dart:
            // 823-843): an uppercase PRIORITY chip, and a drug-interaction glyph when this drug
            // has alerts. Both signals already ride the payload — priority from the dose dialog,
            // vidalAlerts from the persisted round-trip — so this is rendering data we hold, not
            // new plumbing. They now ride the row's `signals` slot (head line) instead of being
            // absolutely overlaid on a DS card that knew nothing about them.
            const priority = med?.priority?.trim();
            const hasInteraction = (med?.vidalAlerts?.length ?? 0) > 0;
            return (
              <ApOrderRow
                key={o.id}
                type={o.type}
                name={o.label}
                code={orderCode(payload)}
                sig={orderDetail(payload)}
                // The design draws the linked Dx as plain mono CODES, primary first — the
                // canvas order of the diagnoses this order justifies.
                dxCodes={dxCodesFor(o.dxIds)}
                // …and this is how the re-link capability survives that (see openLinkEditor).
                onEditDxLinks={!readOnly && onLinkOrderDx ? () => openLinkEditor(o.id) : undefined}
                // PROD-2717: pressing the card (no pencil) reopens the seeded dialog.
                onEdit={!readOnly && onUpdateOrder && orderPayloads ? () => handleEditOrder(o.id) : undefined}
                // The frame v2 Plan (export 21-10 ApOrderRow): edit and remove together at the row's right edge. The card stays pressable.
                editPencil={part === 'plan'}
                onRemove={!readOnly && onRemoveOrder ? () => onRemoveOrder(o.id) : undefined}
                signals={
                  priority || hasInteraction ? (
                    <>
                      {priority && <span className={PRIORITY_CHIP_CLASS}>{priorityLabel(priority)}</span>}
                      {hasInteraction && (
                        <AlertTriangle
                          className="size-4 text-status-error"
                          role="img"
                          aria-label="Drug interaction alert on this medication"
                        />
                      )}
                    </>
                  ) : undefined
                }
              />
            );
          })}
        </section>
      );
    });
  }

  /** Open the re-link editor for an order, seeded with its CURRENT full link set. */
  function openLinkEditor(orderId: string) {
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    setLinkDraft([...order.dxIds]);
    setLinkEditorOrderId(orderId);
  }
  const linkEditorOrder = linkEditorOrderId ? orders.find((o) => o.id === linkEditorOrderId) : undefined;

  const openDx = open ? diagnoses.find((d) => d.id === open.dxId) : undefined;

  // FIX WAVE S6 (PROD-1588): the frame (E1 A&P, node 7624:451752 — ap-frame.png) frames the whole
  // workspace with the SECTION chrome: identity glyph tile + "Assessment & Plan" brand title +
  // the [mic·edit·comment·✦] action row (same anti-fiction set as HPI/ROS/PE — disabled until
  // their backends land). frame="plain": the shell's step region owns the canvas layout, matching
  // how the questionnaire sections mount the chrome.
  /** One diagnosis's identity on the DS card — index, term, code, system, role and the problem-list chips —
   *  shared by A and P so the two parts can never draw a diagnosis differently (design desk, 5 Oct). */
  const dxCardIdentity = (dx: DxCard, index: number) => {
    const onList = matchProblem(patientProblems, dx.code);
    return {
      index,
      term: dx.display,
      code: dx.code ?? '',
      // From the REAL coding, not hardcoded. SNOMED diagnoses are addable
      // (new_ap_bloc.dart:681-693) and were being mislabelled as ICD-10 on every card.
      system: dxCoding?.get(dx.id)?.system ?? 'ICD-10',
      role: dx.isPrimary ? ('Primary' as const) : ('Secondary' as const),
      onList: onList ? { ...(onList.sinceYear != null ? { since: onList.sinceYear } : {}) } : null,
      // Ruling C disclosure: a dx not already on the list joins it at sign — say so.
      // OPT-IN (showProblemListDisclosure, default off): the chip has no counterpart in
      // the Flutter A/P card, so the default frame stays at parity. The DS ties the
      // "Don't add to problem list" action to `autoJoin`, so the opt-out travels with
      // the disclosure — a host either explains the automatic join and offers the way
      // out, or shows neither. It never shows an opt-out for something it never said.
      autoJoin: showProblemListDisclosure && !onList,
      keptOff: keptOffDxIds?.has(dx.id) ?? false,
      resolvedNow: resolvedNowDxIds?.has(dx.id) ?? false,
      // Frame v2 (signal-ds 2.20, PROD-3009): the full diagnosis term wraps instead of clipping to one line,
      // and the code / role meta reads in text-secondary (AA). OFF: no key.
      ...(designV2 ? { termWrap: true, labelTone: 'secondary' as const } : {}),
    };
  };
  // The live app drives THREE of these four (screenshot, Andrew 2026-08-10): the magnifier opens
  // Add-to-favourites, the sparkle opens Add Order; the comment opens the assessment note. The
  // clipboard's Create Bundle is removed (Andrew, 24 Oct: "We are not using it."). Each is LIVE only
  // with its handler. PROD-2946 (#620): the two ORDERING affordances are dropped without their handler (a user off the physician allowlist, or Wrap-up),
  // never drawn as "coming soon" placeholders. Any other action without a handler stays a disabled icon
  // (OFF and the old frame) — and inside the note (`inNote`, frame v2) it is not drawn at all (audit
  // FV2-06, D-60: a gap is never a placeholder).
  // TS-3: A holds the assessment note; P holds Add Order (and Save as order set).
  // PROD-3043 (D-73): inside the note (frame v2) the narrative is typed inline, so no action opens the pop-up.
  // Rule 38 (Andrew's walk, 7 Oct, B10; ON and OFF): after the provider signs (`readOnly`) the ordering affordances —
  // Add Order, Add to favourites and the ⋯ (Save as order set) — are not offered, as without a handler.
  const orderingOpen = !readOnly;
  // `?x=prescription-card`: the Plan's medication orders, each with the data its order row showed (no data removed).
  const prescriptionCards = rxCards ? (
    <PrescriptionCards
      items={orders.filter((o) => o.type === 'medication').map((o): PrescriptionCardItem => {
        const payload = orderPayloads?.get(o.id);
        const med = payload?.kind === 'medication' ? payload : undefined;
        const priority = med?.priority?.trim();
        const hasInteraction = (med?.vidalAlerts?.length ?? 0) > 0;
        return {
          id: o.id,
          name: o.label,
          code: orderCode(payload),
          schedule: medicationSchedule(med, rxSigWordingOn),
          detail: orderDetail(payload),
          dxCodes: dxCodesFor(o.dxIds),
          signals:
            priority || hasInteraction ? (
              <>
                {priority && <span className={PRIORITY_CHIP_CLASS}>{priorityLabel(priority)}</span>}
                {hasInteraction && (
                  <AlertTriangle className="size-4 text-status-error" role="img" aria-label="Drug interaction alert on this medication" />
                )}
              </>
            ) : undefined,
        };
      })}
      onEdit={!readOnly && onUpdateOrder && orderPayloads ? handleEditOrder : undefined}
      onRemove={!readOnly && onRemoveOrder ? onRemoveOrder : undefined}
      // Today's medication search, then the dose dialog, on the primary diagnosis. PA's form links each order to a diagnosis, so with
      // none Add prescription is disabled (a gap the catalog's mock does not have).
      onAdd={orderingOpen && allowedOrderTypes.includes('medication') && defaultDxId ? () => openSeededPicker(defaultDxId, 'medication', undefined, undefined, true) : undefined}
      addDisabledReason={orderingOpen && allowedOrderTypes.includes('medication') && !defaultDxId ? 'Add a diagnosis in Assessment first.' : undefined}
    />
  ) : null;
  const sectionActions: { node: ReactNode; live: boolean }[] = AP_SECTION_ACTIONS.filter(
    (a) =>
      !(part === 'assessment' && a.name === 'sparkle') &&
      !((part === 'plan' || inNote) && a.name === 'message'),
  ).map((a): { node: ReactNode; live: boolean } | null =>
    // PROD-3041 (add-line mode): neither the magnifier ("Add to favourites", a second search entry point)
    // nor the Add Order icon (the same action as the "Add order" line) is drawn: one entry point per action
    // (law L5). They fall through to the dropped branch below. Save as order set stays.
    a.name === 'search' && onAddToFavourites && orderingOpen && !addLineMode
      ? { live: true, node: <SectionIconAction key={a.name} icon={a.glyph} label="Add to favourites" onClick={onAddToFavourites} /> }
      : a.name === 'sparkle' && onOpenAddOrder && orderingOpen && !addLineMode
        ? {
            live: true,
            // Design sheet v1.1 (K2): Add Order is the plus; `sparkle` is reserved for Scrips Intelligence
            // (both modes since the icon pass). The dispatch still keys on the array entry, so Add Order cannot be
            // dropped by the glyph change; without the handler the entry stays the disabled AI one.
            node: <SectionIconAction key={a.name} icon={Plus} label="Add Order" onClick={onOpenAddOrder} />,
          }
        : a.name === 'message' && canEditNote
            ? {
                live: true,
                node: (
                  <SectionIconAction
                    key={a.name}
                    icon={a.glyph}
                    label={assessmentNote.trim() ? 'Edit assessment note' : 'Add an assessment note'}
                    active={noteOpen}
                    onClick={() => setNoteOpen(true)}
                  />
                ),
              }
            : a.name === 'search' || a.name === 'sparkle'
              ? null
              : { live: false, node: <SectionIconAction key={a.name} icon={a.glyph} label={a.label} disabled /> },
  ).filter((action): action is { node: ReactNode; live: boolean } => action !== null).concat(
    // PROD-2722 (board 7): the ⋯ overflow, last in the row, holding "Save as order set".
    saveAsOrderSet && drawsOrders && orderingOpen
      ? [
          {
            live: true,
            node: (
              <SaveAsOrderSetAction
                key="more"
                diagnoses={diagnoses}
                orders={orders}
                payloads={orderPayloads ?? EMPTY_PAYLOADS}
                dxCoding={dxCoding}
              />
            ),
          },
        ]
      : [],
  );
  const chromeActions = sectionActions.map((a) => a.node);
  const liveActions = sectionActions.filter((a) => a.live).map((a) => a.node);

  const chromeBody = (
    <>
      {/* The visit's chief complaint, READ (switch ON only). It sits first because it is THIS visit's
          reason, before the patient's longer-running care plan below; the two are labelled so they
          never read as one claim. Nothing while the read is in flight.
          PROD-3041 (live dev review, 6 Oct; coordinator ruling the same evening): NOT drawn inside the
          note (`inNote`). The note already shows the chief complaint once, in HPI, so a second chip in
          the Diagnoses block repeats a fact (encounter UX law L5: one place per fact). */}
      {designV2 && visitComplaint && drawsDx && !inNote && <ApVisitComplaint state={visitComplaint} className="mb-3" />}
      {/* Care-plan readout (design desk D-14 / D-43; the R6c Plan-section block). Switch ON only
          (`?ef=frameV2`): the frame is the chair's (A7), and OFF keeps today's DOM unchanged. It renders
          nothing with no patient, while loading, or with no care plan. */}
      {designV2 && !part && <CarePlanReadout patientId={patientId} className="mb-3" />}
      {assignCarePlanOn && !part && patientId && <AssignCarePlanAction patientId={patientId} className="mb-3" />}
      {designV2 && !part && <L360ActivationPanel patientId={patientId} className="mb-3" />}
      {/* PROD-3041: the block's ONE add line, first in the block (add-line mode only; read-only after the
          provider sign, rule 38, because `readOnly` is what the host sets then). */}
      {/* `slashInsert` with `?x=-one-order-line` (the old Orders block): the backslash line, once, in the Orders block (this
          instance owns the order dialogs), drawn above "Add order". */}
      {slashInsertOn && !oneOrderLineOn && addLineMode && !readOnly && onAddDx && part !== 'assessment' && (
        <div data-slot="ap-slash-add-line-row">
          <ApSlashAddLine
            providerId={providerId}
            ordersOnly={dxOrderSplitOn}
            allowedOrderTypes={allowedOrderTypes}
            existingCodes={diagnoses.map((d) => d.code ?? '')}
            existingOrderKeys={existingOrderKeys}
            onPickDiagnosis={handleAddLinePick}
            onPickOrder={handleAddLinePick}
          />
        </div>
      )}
      {addLineMode && !readOnly && onAddDx && (
        // Design desk at 0bfe559 (L9, honest empty): a closed add line takes only its own row; the block's own row gap
        // separates it from what follows, with no extra margin of its own.
        <div data-slot="ap-add-line-row">
          {part === 'assessment' ? (
            <ApDiagnosisAddLine
              providerId={providerId}
              inputId={ADD_DIAGNOSIS_INPUT_ID}
              existingCodes={diagnoses.map((d) => d.code ?? '')}
              onPick={handleAddLinePick}
              dxSlash={dxOrderSplitOn}
              aiDiagnosisEnabled={!!aiDiagnosisEnabled}
              complaint={complaintText}
              {...addLineSetSources}
            />
          ) : slashInsertOn && oneOrderLineOn ? (
            /* `slashInsert` (ON on the dev deploy): the Orders block's ONE add line (Samer, 3 Nov: "cannot have multiple
               components"). "Add order" as before; text that starts with "\" is the backslash search, in the same field. */
            <ApOrderSlashAddLine
              providerId={providerId}
              ordersOnly={dxOrderSplitOn}
              allowedOrderTypes={allowedOrderTypes}
              canAddArticle={!!onAddArticle}
              existingCodes={diagnoses.map((d) => d.code ?? '')}
              existingOrderKeys={existingOrderKeys}
              onPickDiagnosis={handleAddLinePick}
              onPickOrder={handleAddLinePick}
              {...addLineSetSources}
            />
          ) : (
            <ApOrderAddLine
              providerId={providerId}
              allowedOrderTypes={allowedOrderTypes}
              canAddArticle={!!onAddArticle}
              existingOrderKeys={existingOrderKeys}
              onPick={handleAddLinePick}
              {...addLineSetSources}
            />
          )}
        </div>
      )}
      {notice && (
        <div role="status" className={STATUS_LINE_CLASS}>
          {notice}
        </div>
      )}

      {/* Flutter's two-column A&P workspace: LEFT selection rail (300px, hidden in read-only) +
          CENTER drag-target canvas (v2: Dx cards on top + a top-level order list below). Drag a
          diagnosis onto the canvas to add a Dx; drag an order anywhere on the canvas to open the
          matching picker SEEDED with the dragged item, attached to the dropped-on card or the
          primary/first Dx (handleDrop → openSeededPicker). The panel's click/keyboard activation is
          the a11y fallback (DnD isn't keyboard-operable). Source: assessment_plan_screen.dart:130. */}
      {dropHint && (
        <p role="status" className={STATUS_LINE_CLASS}>
          {dropHint}
        </p>
      )}
      {/* The held order (`frameV2`): what was picked, what it needs, and the next step. Never a
          silent drop: the clinician can add a diagnosis (the form then opens) or discard it.
          The DS `Banner`, INFO variant (design desk, PR #573): nothing is wrong yet, the order is
          only waiting, and status colour is kept for clinical meaning and real errors. The two
          next steps sit in the Banner's own action slot. */}
      {heldSeed && drawsOrders && (
        <Banner
          variant="info"
          aria-label="Order waiting for a diagnosis"
          className="mb-3"
          action={
            <>
              {!readOnly && onAddDx && categories.includes('diagnosis') ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    // PROD-3041: in add-line mode there is no rail to re-open; the Assessment block's
                    // "Add diagnosis" line takes the focus (the note scrolls to it).
                    if (addLineMode) {
                      // K8: at rest the line is its "Add diagnosis" action (pressing it opens the field, focused).
                      const line = document.getElementById(ADD_DIAGNOSIS_INPUT_ID);
                      if (line instanceof HTMLButtonElement) line.click();
                      else line?.focus();
                      return;
                    }
                    setRailSeed({ category: 'diagnosis', query: '' });
                    setRailKey((k) => k + 1);
                  }}
                >
                  Search diagnoses
                </Button>
              ) : null}
              <Button variant="tertiary" size="sm" onClick={() => onSeedConsumed?.()}>
                Discard order
              </Button>
            </>
          }
        >
          <span className="font-semibold">{heldOrderName(heldSeed.item)}</span>
          {pendingSeedCount > 1
            ? ` and ${pendingSeedCount - 1} more ${pendingSeedCount - 1 === 1 ? 'order are' : 'orders are'} waiting for a diagnosis. `
            : ' is waiting for a diagnosis. '}
          Add the diagnosis {pendingSeedCount > 1 ? 'these orders are' : 'this order is'} for, and{' '}
          {pendingSeedCount > 1 ? 'their forms open one at a time' : 'its form opens'} for you to finish.
        </Banner>
      )}

      {/* The workspace split is the DS `AssessmentPlanLayout` template (DS-D): its rail column is
          `--layout-ap-rail` (299 of content + the 25px gutter, NO rule — PARITY 2026-09-08, measured
          off the deployed Flutter A/P at 1920 / DPR 1) and its canvas column keeps the 20px rhythm.
          The rail is passed only when editable, so its search hooks mount ONLY then — a read-only or
          no-onAddDx A/P calls no query hooks (and so needs no QueryClient), and the DS omits the
          rail column entirely. */}
      <AssessmentPlanLayout
        rail={
          !readOnly && onAddDx && !addLineMode ? (
            <ApSearchRailContainer
              key={railKey}
              {...(railSeed ? { seed: railSeed } : planRailSeed ? { seed: planRailSeed } : {})}
              categories={categories}
              providerId={providerId}
              onActivateItem={handleActivate}
              onAddArticle={onAddArticle}
              aiDiagnosisEnabled={aiDiagnosisEnabled}
              bundles={bundles}
              bundlesLoading={bundlesLoading}
              onImportBundle={onImportBundle}
              onDeleteBundle={onDeleteBundle}
              deletingBundle={deletingBundle}
              orderSets={
                orderSets
                  ? {
                      visitCodes: diagnoses.map((d) => d.code ?? '').filter(Boolean),
                      onApply: (set) => void orderSets.apply(set.id),
                      applyingId: orderSets.applyingId,
                    }
                  : undefined
              }
            />
          ) : undefined
        }
        canvas={
          <>
          {/* ADDED NOTES — the frame places the section narrative at the top of the CANVAS column
              (right of the catalog rail), above DIAGNOSES — and OMITS it while the canvas is empty
              (ap-empty anchor). NOW WIRED (PROD-1801, 30 Jul): the round-trip runs through the
              shell page — hydrated once from the GET, emitted on every save as notes +
              notePlainText. It reads read-only until a host supplies onAssessmentNoteChange. */}
          {/* ADDED NOTES renders ONLY when a narrative actually exists (Andrew, 2026-08-05,
              against the live A/P frame). It used to render on any non-empty canvas, so every
              plan opened with an empty placeholder card claiming a section that had no content —
              chrome pretending to be data. Authoring happens in the popup below, off the section
              header's comment action (Flutter's `commentButton` is a dialog, not an inline card);
              this block is the READ-BACK of a saved note, plus its own edit affordance. */}
          {/* PROD-3043 (design desk D-73): inside the note (frame v2) "Added notes" keeps this place and is
              typed in place, as a note line — same notes Delta + notePlainText, same save; read-only after
              the provider signature. OFF: the read-back card and its pencil, exactly as before. */}
          {drawsDx && inNote ? (
            <ApInlineNarrative
              note={assessmentNote}
              delta={assessmentNoteDelta}
              // Kept while frozen: a draft typed just before the freeze is handed over, never dropped (L7).
              onChange={onAssessmentNoteChange}
              // D-73 amendment: a FORMATTED narrative is not edited inline; "Edit" opens today's dialog.
              onOpenDialog={canEditNote ? () => setNoteOpen(true) : undefined}
              readOnly={!canEditNote}
              macroScope={noteMacroScope}
              macros={noteMacros}
              chartReads={noteChartReads}
              suggested={noteSuggested}
            />
          ) : drawsDx && assessmentNote.trim() && (
            <ApAddedNotes
              value={assessmentNote}
              onEdit={canEditNote ? () => setNoteOpen(true) : undefined}
              onClear={canEditNote ? () => onAssessmentNoteChange?.('', EMPTY_NOTE_DELTA) : undefined}
              readOnly={readOnly}
              placeholder="No assessment narrative yet — use the pencil to add one."
            />
          )}
          {part === 'plan' && diagnoses.length === 0 ? (
            /* TS-3 · P with no diagnosis: the DS canvas would say "Tap the + or Drag & Drop diagnoses",
               but P has no + — diagnoses are written in A. Say where they go instead. */
            <ApEmptyDropLine
              slot="ap-plan-empty"
              text="No diagnosis yet. Add a diagnosis in Assessment. Its orders show here."
              solidOutline={diagnosesBlockOn}
              onDropItem={!readOnly && onAddDx ? (payload) => handleDrop(payload) : undefined}
            />
          ) : part === 'plan' ? (
            /* TS-3 · P: one card per diagnosis (the DS stacked layout, as order sets board 6 draws it),
               each holding the orders that justify it; the diagnosis line itself is read-only here —
               it is written in A. A drop from the search panel still lands on a diagnosis. */
            <AssessmentPlanCanvas
              dxLayout="stacked"
              // Each title once: the note's block already reads "Orders" (the canvas list keeps its own
              // accessible name, "Diagnoses").
              // `""` draws no heading element (signal-ds 2.23, #464), so no empty span takes the list's gap (L9).
              heading=""
              {...(orderSets && planGrouping
                ? {
                    beforeDiagnoses: (
                      <OrderSetCanvasBlocks
                        state={orderSets}
                        diagnoses={diagnoses}
                        readOnly={readOnly}
                        nestedAppKeys={planGrouping.nestedAppKeys}
                        noticeAppKeys={planGrouping.noticeAppKeys}
                      />
                    ),
                  }
                : {})}
              renderDiagnosisBody={(dx: DxCard) => {
                const group = planGrouping?.groups.find((g) => g.dx.id === dx.id);
                if (orderSets) {
                  return (
                    <ApDxOrderBody group={rxCards && group ? { ...group, orders: group.orders.filter((o) => o.type !== 'medication') } : group} renderOrders={renderOrderRuns} state={orderSets} readOnly={readOnly} onRemoveArticle={onRemoveArticle} />
                  );
                }
                if (!group || !group.orders.some((o) => !rxCards || o.type !== 'medication')) return null;
                return (
                  <section data-slot="ap-dx-order-group" aria-label={`Orders for ${dx.display}`} className="flex flex-col gap-2">
                    {renderOrderRuns(group.orders)}
                  </section>
                );
              }}
              // Export 21-10 (EncOrdersByDx): each group opens with a one-line head — the diagnosis number, its term and its
              // code — not the card A draws (this supersedes the design desk's 5 Oct "A and P identical"). Read-only: the
              // diagnosis is written in A. Every diagnosis keeps its head, with or without orders, so it stays a drop target.
              renderDiagnosis={(dx, ctx) => (
                <p data-slot="ap-plan-dx-head" title={dx.display} className="m-0">
                  <span data-part="index">{ctx.index}</span>
                  <span data-part="term" dir="auto">{dx.display}</span>
                  {dx.code ? <span data-part="code" dir="ltr">{dx.code}</span> : null}
                </p>
              )}
              diagnoses={diagnoses}
              orders={[]}
              allowedOrderTypes={allowedOrderTypes}
              roleLabel={roleLabel}
              readOnly={readOnly}
              onDropItem={!readOnly && onAddDx ? handleDrop : undefined}
              aria-label="Plan: orders by diagnosis"
            />
          ) : addLineMode && part === 'assessment' ? (
            /* PROD-3041 · D-75: the Diagnoses block is the cards alone, each 240px or wider, up to 4 columns
               (named container steps: 1 column, 2 from a 512px block, 3 from 768, 4 from 1024; at each step
               a track is at least 244px). Names never truncate (termWrap). Role and Remove are today's:
               the host's priority picker and `onRemoveDx` (its linked-order cascade), absent when read-only.
               The DS canvas grid has a fixed 152px track, so the grid is composed here (a DS gap). */
            diagnoses.length === 0 ? (
              // PROD-3398: with `diagnosesBlock`, an empty block is the Add diagnosis line alone (catalog @62cb3650). Read-only (a
              // signed note) has no Add diagnosis, so it keeps "No diagnoses added" rather than an empty block (#899 review).
              diagnosesBlockOn && !readOnly ? null : (
                <p data-slot="ap-assessment-empty" className="m-0 font-sans text-size-sm text-text-secondary">
                  No diagnoses added
                </p>
              )
            ) : (
              <div className="@container">
                <div role="list" aria-label="Assessment: diagnoses" data-slot="ap-dx-cards" className="grid grid-cols-1 gap-x-4 gap-y-3 @lg:grid-cols-2 @3xl:grid-cols-3 @5xl:grid-cols-4">
                  {diagnoses.map((dx, i) => {
                    const onList = matchProblem(patientProblems, dx.code);
                    const keptOff = keptOffDxIds?.has(dx.id) ?? false;
                    const resolvedNow = resolvedNowDxIds?.has(dx.id) ?? false;
                    return (
                      <div
                        key={dx.id}
                        role="listitem"
                        data-dx-id={dx.id}
                        data-dx-role={dx.isPrimary ? 'primary' : 'secondary'}
                        className={dxBinInlineOn ? 'group/dx-row flex min-w-0 items-start gap-2' : 'min-w-0'}
                      >
                        <EncounterDxCard
                          {...dxCardIdentity(dx, i + 1)}
                          {...(dxBinInlineOn ? { className: 'min-w-0 flex-1' } : {})}
                          onKeepOffList={!readOnly && onKeepOffDx && !onList && !keptOff && !resolvedNow ? () => onKeepOffDx(dx.id) : undefined}
                          onResolveWithVisit={!readOnly && onResolveDxWithVisit && onList && !resolvedNow ? () => onResolveDxWithVisit(dx.id) : undefined}
                          onRoleClick={!readOnly && onSetPrimary ? () => setRolePickerDxId(dx.id) : undefined}
                          onRemove={!dxBinInlineOn && !readOnly && onRemoveDx ? () => onRemoveDx(dx.id) : undefined}
                        />
                        {/* Item 6 (4 Nov): the card's own bin is pinned to its top-right corner, where the long-page row puts the
                            role control, so it drew inside "Primary". With dxBinInline the row draws the bin after the card: the
                            app's icon button, shown on hover or focus of the row (always on touch), as the complaint's bin. */}
                        {dxBinInlineOn && !readOnly && onRemoveDx && (
                          <button
                            type="button"
                            aria-label={`Remove ${dx.display} from this visit`}
                            className="icon-action mt-2 shrink-0 opacity-0 transition-opacity group-hover/dx-row:opacity-100 group-focus-within/dx-row:opacity-100 pointer-coarse:opacity-100"
                            data-slot="ap-dx-remove"
                            onClick={() => onRemoveDx(dx.id)}
                          >
                            <Trash2 className="size-4" aria-hidden />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )
          ) : part === 'assessment' && diagnoses.length === 0 ? (
            /* TS-3 · A with no diagnosis (design desk advisory, 5 Oct): the DS canvas would say "Tap the + or
               Drag & Drop diagnoses", but A has no "+". Name the control by what it SHOWS ("Search" is the
               field's placeholder; "Search diagnoses" is only its aria-label), with no position words
               (WCAG 1.3.3; the layout reflows). A drop from the search still adds a diagnosis here. */
            <ApEmptyDropLine
              slot="ap-assessment-empty"
              text="No diagnosis yet. Use Search to add one."
              solidOutline={diagnosesBlockOn}
              onDropItem={!readOnly && onAddDx ? (payload) => handleDrop(payload) : undefined}
            />
          ) : (
          <AssessmentPlanCanvas
            /* The DIAGNOSIS grid is closed by a hairline rule that separates it from the order run
               beneath it (design 661). Applied through the canvas's own `className` seam rather
               than a wrapper div — the canvas root has no padding/border of its own to collide
               with, and the rule belongs to the block it closes. TS-3 · A: the diagnoses alone. */
            className={canvasGrouping ? undefined : 'pb-4 border-b border-border-default'}
            // TS-3 · A: each title once — the note's block already reads "Diagnoses". No part (OFF): the
            // canvas's own heading, unchanged.
            {...(part === 'assessment' ? { heading: '' } : {})}
            // ORDER SETS (PROD-2723, board 6): each diagnosis ONCE, as a card with its orders, the
            // "No medication checks" notice and each set's "Not ordered from" list nested under it
            // (DS `dxLayout="stacked"`); "Needs a diagnosis", the Undo outcome and the receipt sit
            // under the "Diagnoses & orders" heading. Flag off: the grid and flat list, unchanged.
            {...(canvasGrouping && orderSets
              ? {
                  dxLayout: 'stacked' as const,
                  heading: 'Diagnoses & orders',
                  beforeDiagnoses: (
                    <OrderSetCanvasBlocks
                      state={orderSets}
                      diagnoses={diagnoses}
                      readOnly={readOnly}
                      nestedAppKeys={canvasGrouping.nestedAppKeys}
                      noticeAppKeys={canvasGrouping.noticeAppKeys}
                    />
                  ),
                  renderDiagnosisBody: (dx: DxCard) => (
                    <ApDxOrderBody
                      group={canvasGrouping.groups.find((g) => g.dx.id === dx.id)}
                      renderOrders={renderOrderRuns}
                      state={orderSets}
                      readOnly={readOnly}
                      onRemoveArticle={onRemoveArticle}
                    />
                  ),
                }
              : {})}
            diagnoses={diagnoses}
            // ROUND-4 region 5: orders render OUTSIDE the canvas as per-type sections through the
            // DS ApOrderCard (canonical order-identity accent) — the canvas keeps diagnoses + DnD.
            orders={[]}
            allowedOrderTypes={allowedOrderTypes}
            roleLabel={roleLabel}
            readOnly={readOnly}
            onReorder={onReorder}
            onSetPrimary={onSetPrimary}
            onRemoveDx={onRemoveDx}
            onDropItem={!readOnly && onAddDx ? handleDrop : undefined}
            // FOUR-MOMENTS SWAP (M2b+M3, spec §10.3 — replaces ApDiagnosisCard, retired in the
            // same wave per graduate-and-retire): the DS EncounterDxCard renders each dx cell —
            // identity chip when the dx IS an active problem, "Joins problem list at sign"
            // disclosure + "Don't add to problem list" stewardship opt-out on a NEW dx (ruling C),
            // one-tap "Resolve with this visit" on an on-list dx, keptOff/resolvedNow receipts.
            // The canvas keeps DnD/reorder ownership; the HOST owns the priority picker popup
            // (ModalDropdown below — EncounterDxCard only fires onRoleClick). Width 420 = the
            // Samer-approved canvas frame value (FourMoments Canvas1280 story).
            renderDiagnosis={(dx, ctx) => {
              const onList = matchProblem(patientProblems, dx.code);
              const keptOff = keptOffDxIds?.has(dx.id) ?? false;
              const resolvedNow = resolvedNowDxIds?.has(dx.id) ?? false;
              return (
                /* Since 2026-09-23 the DS `EncounterDxCard` default IS the flat record row this
                   cell used to be flattened into from CSS (`variant="plain"`, fluid width, the full
                   term as its own `title`, and the outlined error bin drawn from `onRemove`), and
                   the card carries its own `data-slot="encounter-dx-card"` — the host hook is gone. */
                /* TS-3 · A (design desk advisory, 5 Oct): the card clamps a long name to one line. The full
                   name shows on hover AND on keyboard focus — a DS Tooltip whose trigger is the card's own
                   focusable wrapper. No part (OFF): the card alone, exactly as before. */
                part === 'assessment' ? (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <div
                          tabIndex={0}
                          data-dx-full-name={dx.display}
                          className="min-w-0 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                        />
                      }
                    >
                    <EncounterDxCard
                      // PROD-2723: inside the stacked canvas's own diagnosis card, the flat record row
                      // (no card-in-a-card). Flag off: the DS default, unchanged.
                      {...(canvasGrouping ? { variant: 'flat' as const } : {})}
                      // index · term · code · system · role · problem-list chips: `dxCardIdentity` (shared
                      // with P, so the two parts can never draw a diagnosis differently).
                      {...dxCardIdentity(dx, ctx.index)}
                      onKeepOffList={
                        !ctx.readOnly && onKeepOffDx && !onList && !keptOff && !resolvedNow
                          ? () => onKeepOffDx(dx.id)
                          : undefined
                      }
                      onResolveWithVisit={
                        !ctx.readOnly && onResolveDxWithVisit && onList && !resolvedNow
                          ? () => onResolveDxWithVisit(dx.id)
                          : undefined
                      }
                      onRoleClick={ctx.onPriorityChange ? () => setRolePickerDxId(dx.id) : undefined}
                      // The DS card draws the outlined error bin (the deployed Flutter A/P's red bin,
                      // Ahmed 2026-09-08 "use trash instead"), named "Remove <term> from this visit",
                      // as the row's last item.
                      onRemove={ctx.onRemove}
                    />
                    </TooltipTrigger>
                    <TooltipContent>{dx.display}</TooltipContent>
                  </Tooltip>
                ) : (
                <EncounterDxCard
                  // PROD-2723: inside the stacked canvas's own diagnosis card, the flat record row
                  // (no card-in-a-card). Flag off: the DS default, unchanged.
                  {...(canvasGrouping ? { variant: 'flat' as const } : {})}
                  // index · term · code · system · role · problem-list chips: `dxCardIdentity` (shared
                  // with P, so the two parts can never draw a diagnosis differently).
                  {...dxCardIdentity(dx, ctx.index)}
                  onKeepOffList={
                    !ctx.readOnly && onKeepOffDx && !onList && !keptOff && !resolvedNow
                      ? () => onKeepOffDx(dx.id)
                      : undefined
                  }
                  onResolveWithVisit={
                    !ctx.readOnly && onResolveDxWithVisit && onList && !resolvedNow
                      ? () => onResolveDxWithVisit(dx.id)
                      : undefined
                  }
                  onRoleClick={ctx.onPriorityChange ? () => setRolePickerDxId(dx.id) : undefined}
                  // The DS card draws the outlined error bin (the deployed Flutter A/P's red bin,
                  // Ahmed 2026-09-08 "use trash instead"), named "Remove <term> from this visit",
                  // as the row's last item.
                  onRemove={ctx.onRemove}
                />
                )
              );
            }}
            aria-label={part === 'assessment' ? 'Assessment: diagnoses' : 'Assessment and plan'}
          />
          )}
          {/* M2b priority picker — the host-owned Primary/Secondary popup (one-primary policy:
              'primary' promotes via onSetPrimary and the host demotes the rest; 'secondary' is a
              deliberate no-op, matching the canvas ctx contract and the retired card's behavior). */}
          <ModalDropdown
            open={rolePickerDxId !== null}
            onClose={() => setRolePickerDxId(null)}
            title="Diagnosis priority"
            options={[
              { label: 'Primary', value: 'primary' },
              { label: 'Secondary', value: 'secondary' },
            ]}
            value={diagnoses.find((d) => d.id === rolePickerDxId)?.isPrimary ? 'primary' : 'secondary'}
            onChange={(p) => {
              // "One diagnosis must be Primary." Flutter blocks demoting the primary and SAYS so
              // (assessment_plan_screen.dart:1453-1460). React treated 'secondary' as a silent no-op,
              // so choosing it on the primary card closed the picker and nothing happened — a dead
              // click with no explanation. The invariant is unchanged; it is now spoken.
              if (p === 'secondary' && rolePickerDxId) {
                const target = diagnoses.find((d) => d.id === rolePickerDxId);
                if (target?.isPrimary) {
                  setPriorityNotice('One diagnosis must be Primary. Promote another diagnosis instead.');
                  setRolePickerDxId(null);
                  return;
                }
              }
              if (p === 'primary' && rolePickerDxId) onSetPrimary?.(rolePickerDxId);
              setPriorityNotice(null);
              setRolePickerDxId(null);
            }}
          />
          {/* Assessment-narrative popup — the section header's comment action opens it.
              The FRAME's editor (Andrew, 2026-08-05): a wide "Add Text Notes" dialog with a
              rich-text toolbar, not a bare textarea. See ap-note-dialog.tsx for why rich text
              is backed by the wire (notes = Quill Delta, notePlainText = flattened). */}
          {/* PROD-3043 (D-73 + amendment): inside the note (frame v2) this opens ONLY from "Edit" on a formatted
              narrative, which the inline line does not rewrite; a plain narrative is typed inline. */}
          <ApNoteDialog
            open={noteOpen}
            onClose={() => setNoteOpen(false)}
            deltaJson={assessmentNoteDelta}
            chartReads={noteChartReads}
            macroScope={noteMacroScope}
            plainText={assessmentNote}
            onSave={(plain, delta) => {
              onAssessmentNoteChange?.(plain, delta);
              setNoteOpen(false);
            }}
          />
          {priorityNotice && (
            <p role="status" className={STATUS_LINE_CLASS}>
              {priorityNotice}
            </p>
          )}
          {/* RE-LINK EDITOR — the capability the design's static Dx text would otherwise have
              deleted (see `linkEditorOrderId`). Same block the order dialogs carry, same min-1
              rule, same `onLinkOrderDx` write; the save mapper and wire contract are untouched
              (meds fan out via rxDiagnosisCodesRequest[], everything else stays single-link). */}
          {linkEditorOrder && (
            <Modal
              open
              onClose={() => setLinkEditorOrderId(null)}
              title={`Diagnosis links — ${linkEditorOrder.label}`}
              size="md"
            >
              <div className="flex flex-col gap-4">
                <DiagnosisMultiSelect
                  diagnoses={dxOptions}
                  selectedIds={linkDraft}
                  onChange={setLinkDraft}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="tertiary" size="sm" onClick={() => setLinkEditorOrderId(null)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    // Min-1: an order must justify at least one diagnosis (Flutter parity, and the
                    // same rule the retired DxLinkChip enforced by locking the last linked chip).
                    disabled={linkDraft.length === 0}
                    onClick={() => {
                      onLinkOrderDx?.(linkEditorOrder.id, linkDraft);
                      setLinkEditorOrderId(null);
                    }}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Modal>
          )}
          {/* The undo receipt (M2b/M3) — bottom of the diagnosis column (approved frame anchor). */}
          {drawsDx && dxReceipt}
          </>
        }
        orders={
          drawsOrders ? (
          <>
          {/* Orders (ROUND-4 region 5). Rows are the LOCAL `ApOrderRow` (ap-order-row.tsx), which
              replaced the DS `ApOrderCard` on this surface for the E1 design
              (EncounterScreen.dc.html:685-723): grey-100 field row, a 3px per-type accent bar from
              the design's blue ramp, and — the substantive change — an explicit TYPE CHIP, because
              the design states at :1008-1010 that order type must be carried by a LABEL, not by
              hue. See the file header for why the DS card could not express it.
              NO per-type HEADING (Andrew, 2026-08-05, against the live A/P frame): the canvas
              carries exactly ONE label — DIAGNOSES — and the orders run as a single continuous
              list beneath the diagnosis row. Rows are still GROUPED by type (same-type orders stay
              together, as the source does) and each run keeps its `aria-label`ed section, so the
              structure survives for assistive tech — only the visible caption is gone. */}
          {!part && !orderSetGrouping && renderOrderRuns(orders)}
          {/* TS-3 · P without order sets: the orders sit in their diagnosis cards; here only the
              orders linked to no diagnosis on the visit. */}
          {part === 'plan' && !orderSets && planGrouping && planGrouping.unlinked.some((o) => !rxCards || o.type !== 'medication') && renderOrderRuns(planGrouping.unlinked)}
          {/* ORDER SETS (PROD-2712 / PROD-2723): the orders nest in the diagnosis cards above
              (the canvas's `renderDiagnosisBody`); here only the orders linked to no diagnosis, and
              the dose dialog's interaction check for the medications a set placed (AC1). */}
          {listGrouping && orderSets && (
            <>
              {/* Orders linked to no diagnosis on the visit: after the cards, as before. */}
              {listGrouping.unlinked.length > 0 && renderOrderRuns(listGrouping.unlinked)}
              <AppliedMedicationChecks
                state={orderSets}
                payloads={orderPayloads}
                diagnoses={diagnoses}
                patientId={patientId}
                providerId={providerId}
                planMedications={orders.flatMap((o) => {
                  const p = orderPayloads?.get(o.id);
                  if (p?.kind !== 'medication' || !p.medicationCode.code) return [];
                  return [{
                    orderId: o.id,
                    med: {
                      code: p.medicationCode.code,
                      display: p.medicationCode.displayName,
                      system: p.medicationCode.system,
                      quantity: p.quantity != null ? p.quantity : '1',
                    },
                  }];
                })}
              />
            </>
          )}
          </>
          ) : undefined
        }
        // Canvas "EDUCATION" section (Flutter assessment_plan_screen.dart:705) — a Dx-less flat
        // list of the attached articles, below the order canvas. Renders nothing when empty.
        // TS-3 · P: the care plan readout follows the orders, then education (R6c Plan order).
        education={
          part === 'assessment' ? undefined : part === 'plan' ? (
            <>
              {rxCards && prescriptionCards}
              {designV2 && <CarePlanReadout patientId={patientId} />}
              {assignCarePlanOn && patientId && <AssignCarePlanAction patientId={patientId} />}
              {designV2 && <L360ActivationPanel patientId={patientId} />}
              {educationSection}
            </>
          ) : (
            educationSection
          )
        }
      >
          {/*
            ACTIVE MEDICATIONS (design 725-765) — this encounter's Rx orders outside the plan
            (Flutter parity; never the chart list, see `activeMedications`). A READ, never plan
            state: nothing here reaches the A/P save mapper, and the section is omitted entirely
            when the host wires no list (no empty-state chrome for a feed that was never asked for).

            ROW = the SAME `ApOrderRow` the plan's orders use (design 728-744 is the order-row
            anatomy verbatim: accent bar · Med chip · name + code · SIG · code line), plus the
            design's four-action trailing stack (745-762). It used to compose the DS
            `MedicationActiveRow`; that molecule draws a white bordered card with a status pill and
            an expandable detail block, which is a different row, so under Andrew's
            match-the-design override it is replaced here alongside `ApOrderCard`.

            THE ACTIONS RENDER ALL FOUR, ALWAYS — and a handler-less one is DISABLED, not hidden
            (the design's cluster is fixed, and a missing button silently changes the row's shape).
            Each is a real server write (PUT Update/{patientRxOrderId} · PostRefill ·
            PostStopMedication on Scrips.Patient EPriscriptionController) and only the STOP path
            has a React hook in this repo so far (services/use-save-medication.ts
            `buildStopMedicationBody`). Disabled keeps both promises at once: the design's shape,
            and no button that looks live while doing nothing.
          */}
          {drawsOrders && (activeMedications?.length ?? 0) > 0 && (
            <section aria-label="Active medications" className="flex flex-col gap-2">
              <span className={designV2 ? SECTION_HEADING_12_CLASS_V2 : SECTION_HEADING_12_CLASS}>Active Medications</span>
              {(activeMedications ?? []).map((m) => (
                <ApOrderRow
                  key={m.id}
                  type="medication"
                  name={m.name}
                  sig={m.sig || m.detail || undefined}
                  aria-label={`Active medication: ${m.name}`}
                  // No Dx-code line: these rows are not plan orders and hold no A/P diagnosis
                  // links (an Rx's own ordered-for rides `orderedFor`, not drawn here). The
                  // design's fixture shows codes; inventing them here would be fabricating a
                  // clinical linkage the server never sent.
                  actions={
                    <ApMedLifecycleActions
                      onChange={!readOnly && onChangeMedication ? () => onChangeMedication(m.id) : undefined}
                      onRefill={!readOnly && onRefillMedication ? () => onRefillMedication(m.id) : undefined}
                      onDiscontinue={
                        !readOnly && onDiscontinueMedication ? () => onDiscontinueMedication(m.id) : undefined
                      }
                      onDelete={!readOnly && onDeleteMedication ? () => onDeleteMedication(m.id) : undefined}
                    />
                  }
                />
              ))}
            </section>
          )}
      </AssessmentPlanLayout>

      {/* Medication picker — composes the DS MedicationSearchWidget; opens for the Dx whose
          "+ Med" was tapped. Wrapped in the DS Modal (same pattern as the other add dialogs) so it
          can never render inline in the canvas — even on a no-seed path. Live search via the host. */}
      {openDx && open?.type === 'medication' && !pickedMed && (
        <Modal open onClose={closePicker} title="Add medication" size="md">
          <section className={designV2 ? CARD_CLASS_V2 : CARD_CLASS} aria-label="Add medication">
            <EncounterSectionHeader title={`Add Medication — ${openDx.display}`} variant="section" />
            <div className="h-2" />
            <MedicationSearchWidget
              value={medicationQuery ?? ''}
              onChange={(q) => onMedicationQueryChange?.(q)}
              results={medicationResults ?? []}
              onResultClick={(result) => {
                // Step 2 (PROD-1167): capture a structured dose before attaching. The coded
                // medication order is surfaced via onAttachOrder (like the other dialogs);
                // the legacy onAttachMedication path is superseded by the dose dialog.
                if (onAttachOrder) setPickedMed(result);
                else {
                  onAttachMedication?.(openDx.id, result); // fallback: host without onAttachOrder
                  closePicker();
                }
              }}
              placeholder="Search medications…"
            />
            <div className="flex justify-end mt-3">
              <Button variant="tertiary" size="sm" onClick={closePicker}>Cancel</Button>
            </div>
          </section>
        </Modal>
      )}

      {/* Step 2 — structured dose (route / frequency / duration / sig) for the picked med. */}
      {openDx && open?.type === 'medication' && pickedMed && (
        <MedicationDoseDialog
          // key by the picked med so re-selecting a different medication remounts with a fresh
          // pre-fill (route/units) instead of persisting the prior med's values (Flutter clears
          // these on re-select; CoS review PROD-1340).
          key={pickedMed.id}
          medication={pickedMed}
          idSuffix={orderSeq}
          // PROD-1801: the DIAGNOSIS (n) * block. `dxOptions`, not `diagnoses` — that prop is already
          // taken on this dialog for drug-interaction context.
          dxOptions={dxOptions}
          initialDxIds={initialDxFor(openDx.id)}
          // An EDIT reconstructs the saved order; otherwise a dictated sig pre-fills a new one.
          // Edit wins — a saved order is a fact and the dictation is a proposal, so the two can
          // never both apply to the same dialog. The dialog falls through per field
          // (`initialDose?.route ?? medication.route?.[0]?.code`), so omitting route and dose unit
          // from the dictated object leaves the PICKED record's coded values in place.
          initialDose={
            open.editPayload?.kind === 'medication'
              ? payloadToMedicationDose(open.editPayload)
              : open.dictatedDose
          }
          editing={!!open.editingOrderId}
          // Inline drug-interaction check (Flutter getDrugInteractions): patient + prescriber +
          // the working diagnoses (drug-disease context). Only the coded diagnoses are sent.
          patientId={patientId}
          providerId={providerId}
          diagnoses={diagnoses
            .filter((d) => !!d.code)
            .map((d) => ({ identifier: d.id, code: d.code as string, display: d.display }))}
          // The other meds already on the plan (Flutter apData.medications()) — the dialog prepends
          // the med being entered so drug-drug pairs within the draft are checked, not just the new med.
          existingMedications={existingMedications}
          onConfirm={(o) => handleAttach(o.dxIds, o.planOrder, o.payload)}
          onDelete={deleteEditingOrder}
          // Cancel closes the WHOLE med picker (clears `open` + `pickedMed`) — else
          // `open.type==='medication' && !pickedMed` would re-render the search step in the canvas.
          onCancel={closePicker}
        />
      )}

      {/* Lab picker (inline panel) — owns useLabSearch + useLabSubTests internally. Seeded with the
          dragged study when the drop carried one (PROD-1419). */}
      {openDx && open?.type === 'lab' && (
        <LabAddDialog
          // key by the seeded study so dropping a DIFFERENT lab while this dialog is open remounts it
          // with the new seed (the dialog seeds `selected` via useState — Codex Medium, PROD-1419).
          key={`lab-${(open.initialItem as CodeSearchResult | undefined)?.id ?? 'new'}`}
          providerId={providerId}
          gender={gender}
          idSuffix={orderSeq}
          diagnoses={dxOptions}
          initialDxIds={initialDxFor(openDx.id)}
          initialItem={open.initialItem as CodeSearchResult | undefined}
          initialSubTests={open.editPayload?.kind === 'lab' ? open.editPayload.subTests : undefined}
          initialOmittedTests={open.editPayload?.kind === 'lab' ? open.editPayload.omittedTests : undefined}
          initialNote={open.editPayload?.kind === 'lab' ? open.editPayload.note : undefined}
          initialVendor={open.editPayload?.kind === 'lab' ? open.editPayload.vendor : undefined}
          initialPanelDefinitionId={open.editPayload?.kind === 'lab' ? open.editPayload.panelDefinitionId : undefined}
          initialPanelVersion={open.editPayload?.kind === 'lab' ? open.editPayload.panelVersion : undefined}
          initialBillTo={open.editPayload?.kind === 'lab' ? open.editPayload.billTo : undefined}
          initialAlreadyOrderedTests={open.editPayload?.kind === 'lab' ? open.editPayload.alreadyOrderedTests : undefined}
          initialOrderSearch={open.editPayload?.kind === 'lab' ? open.editPayload.orderSearch : undefined}
          initialAoeAnswers={open.editPayload?.kind === 'lab' ? open.editPayload.aoeAnswers : undefined}
          otherLabs={otherLabs}
          editing={!!open.editingOrderId}
          onConfirm={(o) => handleAttach(o.dxIds, o.planOrder, o.payload)}
          onDelete={deleteEditingOrder}
          onCancel={closePicker}
        />
      )}

      {/* Imaging / radiology picker (inline panel) — owns useRadiologySearch internally. */}
      {openDx && open?.type === 'imaging' && (
        <ImagingAddDialog
          key={`imaging-${(open.initialItem as CodeSearchResult | undefined)?.id ?? 'new'}`}
          providerId={providerId}
          idSuffix={orderSeq}
          // PROD-1801: the dialog owns the DIAGNOSIS (n) * multi-select, so it needs the encounter's
          // diagnoses and returns the full linked set. Seeded with the Dx the picker was opened from,
          // so the ordinary single-Dx flow costs no extra clicks.
          diagnoses={dxOptions}
          initialDxIds={initialDxFor(openDx.id)}
          initialItem={open.initialItem as CodeSearchResult | undefined}
          initialNote={open.editPayload?.kind === 'imaging' ? open.editPayload.note : undefined}
          editing={!!open.editingOrderId}
          onConfirm={(o) => handleAttach(o.dxIds, o.planOrder, o.payload)}
          onDelete={deleteEditingOrder}
          onCancel={closePicker}
        />
      )}

      {/* Procedure picker (DS Modal) — owns useProcedureSearch (CPT/SNOMED) internally. */}
      {openDx && open?.type === 'procedure' && (
        <ProcedureAddDialog
          key={`procedure-${(open.initialItem as CodeSearchResult | undefined)?.id ?? 'new'}`}
          providerId={providerId}
          orderSeq={orderSeq}
          diagnoses={dxOptions}
          initialDxIds={initialDxFor(openDx.id)}
          // PROD-3184: an unmatched AMBIENT procedure opens on the search, filled in with its name.
          // Every other seed (a drag, a rail pick, a matched suggestion, an edit) is a pick, as before.
          {...(ambientSearchText(open.initialItem) && !open.editingOrderId
            ? { initialQuery: ambientSearchText(open.initialItem) }
            : { initialItem: open.initialItem as CodeSearchResult | undefined })}
          initialAddToBilling={open.editPayload?.kind === 'procedure' ? open.editPayload.addToBilling : undefined}
          initialNote={open.editPayload?.kind === 'procedure' ? open.editPayload.note : undefined}
          editing={!!open.editingOrderId}
          onConfirm={(o) => handleAttach(o.dxIds, o.planOrder, o.payload)}
          onDelete={deleteEditingOrder}
          onCancel={closePicker}
        />
      )}

      {/* Referral picker (DS Modal) — owns useReferralSearch (additional diagnoses) internally. The
          dragged referral target seeds the dialog's `target` (adapted from the CodeSearchResult). */}
      {openDx && open?.type === 'referral' && (
        <ReferralAddDialog
          open
          providerId={providerId}
          orderIndex={orderSeq}
          // An edit seeds from the saved payload (a speciality referral keeps speciality + practitioner); new from the picked item.
          {...referralDialogSeed(open.editPayload, referralTargetFromItem(open.initialItem))}
          editing={!!open.editingOrderId}
          // No diagnosis picker here, so an edit keeps the order's own links (PROD-2886).
          onConfirm={(o) => handleAttach(editingOrderDxIds ?? [openDx.id], o.planOrder, o.payload)}
          onCancel={closePicker}
        />
      )}

      {/* Vaccine picker (DS Modal) — owns useVaccinationSearch; 2-step search → request editor
          (PROD-1227). Seeded with the dragged vaccine (ImmunizationCoding) → opens straight in the
          editor. Attaches a 'vaccination' canvas order that round-trips via to-/from-patient. */}
      {openDx && open?.type === 'vaccination' && (() => {
        /*
          THE RAIL NOW EMITS A FULL DDC VaccineValueSet, a favourite still emits a bare coding
          (PROD-1819). Route by SHAPE, because `initialItem` is `unknown` in OpenPicker and typed
          per-branch here — so a mis-cast is invisible to the compiler:
            • VaccineValueSet (has `valueCoding`) → `initialValueSet`, keeping route[] + uom, no lookup
            • bare coding                        → `initialItem`, and the dialog's by-code catalogue
                                                   lookup fills route/uom (Flutter fetchSelectedVaccinationData)
          Casting a value-set to ImmunizationCoding would read `.code` off the wrong level and seed an
          EMPTY code — which then looks exactly like a product with no stock.
        */
        const seed = open.initialItem as VaccineValueSet | ImmunizationCoding | undefined;
        const seedIsValueSet = !!seed && typeof seed === 'object' && 'valueCoding' in seed;
        const railValueSet = seedIsValueSet ? (seed as VaccineValueSet) : undefined;
        const railCoding = seedIsValueSet ? undefined : (seed as ImmunizationCoding | undefined);
        const seedCode = railValueSet?.valueCoding.code ?? railCoding?.code;
        return (
        <VaccineAddDialog
          key={`vaccine-${seedCode ?? 'new'}`}
          orderSeq={orderSeq}
          dxOptions={dxOptions}
          initialDxIds={initialDxFor(openDx.id)}
          initialItem={railCoding}
          initialValueSet={open.editPayload?.kind === 'vaccination' ? vaccinationEntryToValueSet(open.editPayload.entry) : railValueSet}
          initialDraft={open.editPayload?.kind === 'vaccination' ? vaccinationEntryToDraft(open.editPayload.entry) : undefined}
          editing={!!open.editingOrderId}
          onConfirm={(o) => handleAttach(o.dxIds, o.planOrder, o.payload)}
          onDelete={deleteEditingOrder}
          onCancel={closePicker}
        />
        );
      })()}
    </>
  );
  // TS-3 · A or P: each title once (design desk, 5 Oct). The note's section ("Assessment" / "Plan") and
  // its block ("Diagnoses" / "Orders") already name the part, so the part draws its actions with no
  // title of its own. Inside the note (`inNote`) only the LIVE actions are drawn (audit FV2-06).
  // No part (OFF, and every other caller): the section chrome, exactly as before.
  if (part) {
    return (
      <ApPartFrame label={part === 'assessment' ? 'Assessment' : 'Plan'} actions={inNote ? liveActions : chromeActions} dropEmptyToolbar={!!inNote && designV2}>
        {chromeBody}
      </ApPartFrame>
    );
  }

  return (
    <DsEncounterSectionChrome
      title="Assessment & Plan"
      icon="assessment-plan"
      frame="plain"
      titleTone="depth"
      // Design sheet v1.1 (switch ON only, DS 2.14.0 DCR-01): the solid wayfinding badge.
      {...(designV2 ? { badge: 'solid' as const } : {})}
      data-testid="ap-section-chrome"
      /* The comment action is LIVE when the host supplies a note handler — Flutter's
         `commentButton` opens the assessment-narrative dialog (assessment_plan_screen.dart).
         The rest of the row stays visible-but-disabled (anti-fiction: no backend yet). */
      actions={chromeActions}
    >
      {chromeBody}
    </DsEncounterSectionChrome>
  );
}
